import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// Debug HUD: enable by adding ?debug=1 or setting window.__STUDIO_DEBUG__ = true in console.
// HUD shows drag state + coords + hovered frame to verify drop-to-fill pipeline.
import html2canvas from 'html2canvas';
import { Link, useHistory, useParams } from 'react-router-dom';
import StudioPage from './StudioPage';
import EditorOnlyLayout from './components/EditorOnlyLayout';
import EditorTopBar from './components/EditorTopBar';
import CanvasStage from './components/CanvasStage';
import LayersPanel from './components/LayersPanel';
import PropertiesPanel from './components/properties/PropertiesPanel';
import ResizeCanvasModal from './components/ResizeCanvasModal';
import useStageControls from './hooks/useStageControls';
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';
import useDebouncedCommit from './hooks/useDebouncedCommit';
import useStudioAssets from './hooks/useStudioAssets';
import { getProject, updateProject } from './utils/studioProjectRepo';
import {
  getBoundsFromElements,
  getViewportPoint,
  viewportToWorld,
  worldToViewport,
} from './utils/coords';
import { buildSnapGuides, getSnapDelta } from './utils/snapGuides';
import { cloneElementsWithOffset, normalizeClipboard } from './utils/cloneElements';
import { createElementId } from './utils/elementPatch';
import { measureTextBox } from './utils/textMeasure';
import useFranchiseeWorkspace from './utils/useFranchiseeWorkspace';
import { normalizeElements } from './utils/elementDefaults';
import { sanitizeDeep } from './utils/sanitize';
import { driveDownloadBlobUrl, driveUpload, startGoogleDriveAuth } from '../brandingMarketing/driveApi';
import { clampFrameContent, computeCoverScale, createFrameContentFromAsset } from './utils/frameUtils';
import { clampCropRect, updateCropRect, getNormalizedPoint } from './canvas/tools/cropTool';
import {
  addPointToStroke,
  createStroke,
  getNormalizedPointFromBounds,
} from './canvas/tools/eraserTool';
import {
  createMagicOp,
  mapWorldPointToImageNormalized,
} from './canvas/tools/magicEraserTool';
import {
  DEFAULT_PAGE_SETTINGS,
  mergePageSettings,
  normalizePageSettings,
} from './utils/pageSettings';
import { normalizePaint } from './utils/paintUtils';
import { insertCatalogItem } from './elements/insertElementFromCatalog';
import { normalizePalette, addRecentPaint, collectDocumentColors } from './utils/paletteUtils';
import { extractPhotoColors } from './utils/photoColors';
import { readProjectCache, writeProjectCache } from './utils/localProjectCache';
import './styles/StudioEditor.css';

const createDemoElements = (width, height) => {
  const safeWidth = Math.max(width, 600);
  const safeHeight = Math.max(height, 400);
  const titleMeasure = measureTextBox({
    text: 'Your Title',
    fontFamily: 'Inter, sans-serif',
    fontSize: 48,
    fontWeight: 600,
    fontStyle: 'normal',
    lineHeight: 1.1,
    letterSpacing: 0,
    autoWidth: true,
    paddingX: 0,
    paddingY: 0,
    minW: 40,
  });
  const subtitleMeasure = measureTextBox({
    text: 'Add your content',
    fontFamily: 'Inter, sans-serif',
    fontSize: 22,
    fontWeight: 400,
    fontStyle: 'normal',
    lineHeight: 1.4,
    letterSpacing: 0,
    autoWidth: true,
    paddingX: 0,
    paddingY: 0,
    minW: 40,
  });
  return [
    {
      id: 'demo-shape-1',
      type: 'shape',
      shapeType: 'rect',
      x: Math.round(safeWidth * 0.12),
      y: Math.round(safeHeight * 0.18),
      w: Math.round(safeWidth * 0.32),
      h: Math.round(safeHeight * 0.22),
      rotation: 0,
      opacity: 1,
      locked: false,
      zIndex: 1,
      fill: '#e1dccf',
      stroke: '#c9a962',
      strokeWidth: 2,
      radius: 12,
    },
    {
      id: 'demo-text-1',
      type: 'text',
      x: Math.round(safeWidth * 0.12),
      y: Math.round(safeHeight * 0.1),
      w: Math.round(titleMeasure.tightW),
      h: Math.round(titleMeasure.tightH),
      rotation: 0,
      opacity: 1,
      locked: false,
      zIndex: 3,
      text: 'Your Title',
      fontFamily: 'Inter, sans-serif',
      fontSize: 48,
      fontWeight: 600,
      fontStyle: 'normal',
      underline: false,
      color: '#111',
      align: 'left',
      lineHeight: 1.1,
      letterSpacing: 0,
      paddingX: 0,
      paddingY: 0,
      autoWidth: true,
      autoHeight: true,
      minW: 40,
    },
    {
      id: 'demo-text-2',
      type: 'text',
      x: Math.round(safeWidth * 0.12),
      y: Math.round(safeHeight * 0.25),
      w: Math.round(subtitleMeasure.tightW),
      h: Math.round(subtitleMeasure.tightH),
      rotation: 0,
      opacity: 0.9,
      locked: false,
      zIndex: 2,
      text: 'Add your content',
      fontFamily: 'Inter, sans-serif',
      fontSize: 22,
      fontWeight: 400,
      fontStyle: 'normal',
      underline: false,
      color: '#333',
      align: 'left',
      lineHeight: 1.4,
      letterSpacing: 0,
      paddingX: 0,
      paddingY: 0,
      autoWidth: true,
      autoHeight: true,
      minW: 40,
    },
  ];
};

const autoFitTextElements = (elements = []) => {
  let changed = false;
  const next = elements.map((el) => {
    if (el.type !== 'text') return el;
    const autoWidth = el.autoWidth !== false;
    const autoHeight = el.autoHeight !== false;
    if (!autoWidth && !autoHeight) return el;
    const measure = measureTextBox({
      text: el.text,
      wrapWidth: autoWidth ? undefined : el.w,
      autoWidth,
      fontSize: el.fontSize || 20,
      fontFamily: el.fontFamily || 'Inter, sans-serif',
      fontWeight: el.fontWeight || 400,
      fontStyle: el.fontStyle || 'normal',
      lineHeight: el.lineHeight || 1.2,
      letterSpacing: el.letterSpacing || 0,
      paddingX: el.paddingX || 0,
      paddingY: el.paddingY || 0,
      minW: el.minW || 40,
    });
    const nextW = autoWidth ? measure.tightW : el.w;
    const nextH = autoHeight ? measure.tightH : el.h;
    if (nextW !== el.w || nextH !== el.h) {
      changed = true;
    }
    return {
      ...el,
      w: nextW,
      h: nextH,
    };
  });
  return { elements: next, changed };
};

const rgbToHex = (r, g, b) => {
  const toHex = (value) => Math.max(0, Math.min(255, value))
    .toString(16)
    .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
};

const StudioEditorPage = ({ firebase, brandingContextLoading, mode = 'dashboard' }) => {
  const history = useHistory();
  const { projectId } = useParams();
  const { franchiseeId, uid, loading: workspaceLoading } = useFranchiseeWorkspace();
  const [project, setProject] = useState(null);
  const [loadingProject, setLoadingProject] = useState(true);
  const [projectError, setProjectError] = useState('');
  const [editorPages, setEditorPages] = useState([]);
  const [activePageId, setActivePageId] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeTool, setActiveTool] = useState('text');
  const [pageSettings, setPageSettings] = useState(DEFAULT_PAGE_SETTINGS);
  const [isBackgroundRepositioning, setIsBackgroundRepositioning] = useState(false);
  const [palette, setPalette] = useState(() => normalizePalette({}));
  const [photoColors, setPhotoColors] = useState([]);
  const [eyedropperActive, setEyedropperActive] = useState(false);
  const [saveStatus, setSaveStatus] = useState('Saved');
  const [saveError, setSaveError] = useState('');
  const [saveDebug, setSaveDebug] = useState('');
  const [exportStatus, setExportStatus] = useState('');
  const [exportError, setExportError] = useState('');
  const [demoElements, setDemoElements] = useState([]);
  const [editingTextId, setEditingTextId] = useState(null);
  const [editingTextValue, setEditingTextValue] = useState('');
  const [editingTextOriginal, setEditingTextOriginal] = useState('');
  const [interactionMode, setInteractionMode] = useState('idle');
  const [marquee, setMarquee] = useState(null);
  const [resizeHud, setResizeHud] = useState(null);
  const [snapGuides, setSnapGuides] = useState({ x: [], y: [] });
  const [isEditingName, setIsEditingName] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [resizeModalOpen, setResizeModalOpen] = useState(false);
  const [resizeWidth, setResizeWidth] = useState('');
  const [resizeHeight, setResizeHeight] = useState('');
  const [resizeUnit, setResizeUnit] = useState('px');
  const [resizeScaleContent, setResizeScaleContent] = useState(false);
  const [resizeErrors, setResizeErrors] = useState({});
  const [imageToolPanel, setImageToolPanel] = useState({ open: false, tool: null, targetId: null });
  const [cropDraft, setCropDraft] = useState(null);
  const [frameCropState, setFrameCropState] = useState({
    active: false,
    frameId: null,
    draftContent: null,
  });
  const dragAssetRef = useRef({
    active: false,
    armed: false,
    type: null,
    payload: null,
    pointerId: null,
    startClientX: 0,
    startClientY: 0,
    clientX: 0,
    clientY: 0,
    hoveredFrameId: null,
  });
  const pageBackgroundDraftRef = useRef(null);
  const pageBackgroundCommitRef = useRef(null);
  const pageBackgroundOriginalRef = useRef(null);
  const isSpacePressedRef = useRef(false);
  const dragRafRef = useRef(null);
  const dragLogRef = useRef(0);
  const [dragUI, setDragUI] = useState({
    active: false,
    armed: false,
    clientX: 0,
    clientY: 0,
    worldX: 0,
    worldY: 0,
    hoveredFrameId: null,
  });
  const [dragDebug, setDragDebug] = useState({
    lastDropAction: 'none',
    lastError: '',
    canvasRect: null,
  });
  const debugEnabled = useMemo(() => {
    if (process.env.NODE_ENV === 'production') return false;
    const urlHasDebug = typeof window !== 'undefined'
      && window.location
      && window.location.search.includes('debug=1');
    return Boolean(typeof window !== 'undefined' && (window.__STUDIO_DEBUG__ || urlHasDebug));
  }, []);
  const [activeEraserStroke, setActiveEraserStroke] = useState(null);
  const [eraserCursor, setEraserCursor] = useState(null);
  const [resizeSaving, setResizeSaving] = useState(false);
  const {
    assets,
    loading: assetsLoading,
    error: assetsError,
    connected: driveConnected,
    needsReconnect: driveNeedsReconnect,
    rootFolderId: studioRootFolderId,
    refresh: refreshDriveAssets,
  } = useStudioAssets(firebase);
  const demoSeededRef = useRef(false);
  const saveTimerRef = useRef(null);
  const localSaveTimerRef = useRef(null);
  const projectCacheRef = useRef(null);
  const saveInFlightRef = useRef(false);
  const saveDirtyRef = useRef(false);
  const lastSaveAtRef = useRef(0);
  // NOTE: stage transform order is translate(panX, panY) scale(zoom) with origin 0 0.
  // screen -> world uses the inverse of that exact order.
  const viewportRef = useRef(null);
  const stageRef = useRef(null);
  const artboardRef = useRef(null);
  const interactionRef = useRef(null);
  const isInteractingRef = useRef(false);
  const clipboardRef = useRef({ elements: [], offset: 0 });
  const lastClickRef = useRef({ id: null, time: 0 });
  const eyedropperActiveRef = useRef(false);
  const eyedropperCallbackRef = useRef(null);
  const isStandalone = mode === 'standalone';
  const studioHomePath = '/franchise-dashboard/studio';
  const currentUserEmail = firebase?.auth?.currentUser?.email || '';
  const handleConnectDrive = useCallback(async () => {
    if (!firebase) return;
    const email = currentUserEmail;
    const domain = email.includes('@') ? email.split('@')[1] : '';
    const url = await startGoogleDriveAuth(firebase, {
      loginHint: email || undefined,
      hd: domain || undefined,
    });
    window.location.assign(url);
  }, [currentUserEmail, firebase]);

  useEffect(() => {
    let mounted = true;
    if (!franchiseeId || !projectId || !firebase) {
      setLoadingProject(false);
      return () => {
        mounted = false;
      };
    }

    const loadProject = async () => {
      setLoadingProject(true);
      setProjectError('');
      try {
        const cached = await readProjectCache(projectId);
        if (cached?.data && mounted) {
          const cachedDoc = cached.data;
          const cachedPages = Array.isArray(cachedDoc.pages) && cachedDoc.pages.length
            ? cachedDoc.pages
            : [{ id: cachedDoc.activePageId || 'page-1', elements: [] }];
          const cachedNormalized = cachedPages.map((page) => ({
            ...page,
            elements: normalizeElements(page.elements || []),
            groups: Array.isArray(page.groups) ? page.groups : [],
          }));
          const cachedActive = cachedDoc.activePageId || cachedPages[0].id;
          const cachedSettings = normalizePageSettings(cachedDoc.pageSettings);
          const cachedPalette = normalizePalette(cachedDoc.palette || {});
          setProject({
            ...cachedDoc,
            pages: cachedNormalized,
            activePageId: cachedActive,
            pageSettings: cachedSettings,
          });
          projectCacheRef.current = {
            ...cachedDoc,
            pages: cachedNormalized,
            activePageId: cachedActive,
            pageSettings: cachedSettings,
            palette: cachedPalette,
          };
          setPageSettings(cachedSettings);
          setPalette(cachedPalette);
          setEditorPages(cachedNormalized);
          setActivePageId(cachedActive);
          setSelectedIds([]);
          setDraftName(cachedDoc.name || 'Untitled Design');
          setIsEditingName(false);
          demoSeededRef.current = false;
          setSaveStatus('Saved');
        }
        const doc = await getProject(firebase, franchiseeId, projectId);
        if (!mounted) return;
        if (!doc) {
          setProject(null);
          setProjectError("Design not found or you don't have access.");
          return;
        }
        const pages = Array.isArray(doc.pages) && doc.pages.length
          ? doc.pages
          : [{ id: doc.activePageId || 'page-1', elements: [] }];
        const normalizedPages = pages.map((page) => ({
          ...page,
          elements: normalizeElements(page.elements || []),
          groups: Array.isArray(page.groups) ? page.groups : [],
        }));
        let didAutoFit = false;
        const fittedPages = normalizedPages.map((page) => {
          const fit = autoFitTextElements(page.elements || []);
          if (fit.changed) {
            didAutoFit = true;
            return { ...page, elements: fit.elements };
          }
          return page;
        });
        const activeId = doc.activePageId || pages[0].id;
        const normalizedPageSettings = normalizePageSettings(doc.pageSettings);
        const normalizedPalette = normalizePalette(doc.palette || {});
        setProject({
          ...doc,
          pages: fittedPages,
          activePageId: activeId,
          pageSettings: normalizedPageSettings,
        });
        projectCacheRef.current = {
          ...doc,
          pages: fittedPages,
          activePageId: activeId,
          pageSettings: normalizedPageSettings,
          palette: normalizedPalette,
        };
        writeProjectCache(projectId, projectCacheRef.current).catch((error) => {
          console.error('Failed to refresh local studio cache:', error);
        });
        setPageSettings(normalizedPageSettings);
        setPalette(normalizedPalette);
        setEditorPages(fittedPages);
        setActivePageId(activeId);
        setSelectedIds([]);
        setDraftName(doc.name || 'Untitled Design');
        setIsEditingName(false);
        demoSeededRef.current = false;
        setSaveStatus('Saved');
        if (didAutoFit) {
          updateProject(firebase, franchiseeId, projectId, sanitizeDeep({
            pages: fittedPages,
            activePageId: activeId,
          })).catch((error) => {
            console.error('Failed to auto-fit text elements:', error);
          });
        }
      } catch (error) {
        if (!mounted) return;
        console.error('Failed to load studio project:', error);
        setProjectError('Unable to load design.');
      } finally {
        if (mounted) {
          setLoadingProject(false);
        }
      }
    };

    loadProject();
    return () => {
      mounted = false;
    };
  }, [firebase, franchiseeId, projectId]);

  useEffect(() => {
    if (!firebase || !franchiseeId || !projectId || !project) return;
    updateProject(firebase, franchiseeId, projectId, {
      lastOpenedAt: firebase.fieldValue.serverTimestamp(),
    }).catch((error) => {
      console.error('Failed to update lastOpenedAt:', error);
    });
  }, [firebase, franchiseeId, project, projectId]);

  const artboardWidth = Number(project?.width) || 1080;
  const artboardHeight = Number(project?.height) || 1080;


  const showGuides = pageSettings?.showGuides ?? true;
  const showRulers = pageSettings?.showRulers ?? true;

  const stageControls = useStageControls({
    viewportRef,
    artboardWidth,
    artboardHeight,
  });

  const backgroundImage = pageSettings?.backgroundImage;
  const backgroundImageEnabled = Boolean(backgroundImage?.enabled && backgroundImage?.src);
  const backgroundImageForRender = useMemo(() => {
    if (!backgroundImageEnabled || !backgroundImage) return backgroundImage;
    return clampBackgroundImage(backgroundImage);
  }, [backgroundImage, backgroundImageEnabled, clampBackgroundImage]);

  const startEyedropper = useCallback((applyPaint) => {
    if (!applyPaint) return;
    eyedropperCallbackRef.current = applyPaint;
    eyedropperActiveRef.current = true;
    setEyedropperActive(true);
  }, []);

  const finishEyedropper = useCallback(() => {
    eyedropperCallbackRef.current = null;
    eyedropperActiveRef.current = false;
    setEyedropperActive(false);
  }, []);


  const pickEyedropperColor = useCallback(async (event) => {
    const applyPaint = eyedropperCallbackRef.current;
    finishEyedropper();
    if (!applyPaint || !artboardRef.current) return;
    const rect = artboardRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
      return;
    }
    try {
      const canvas = await html2canvas(artboardRef.current, {
        backgroundColor: null,
        useCORS: true,
        logging: false,
        scale: 1,
      });
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const px = Math.floor(x * scaleX);
      const py = Math.floor(y * scaleY);
      const data = ctx.getImageData(px, py, 1, 1).data;
      const [r, g, b, a] = data;
      if (a === 0) return;
      const alpha = Math.round((a / 255) * 100);
      applyPaint(normalizePaint({ type: 'solid', color: rgbToHex(r, g, b), alpha }));
    } catch (error) {
      console.error('Eyedropper failed:', error);
    }
  }, [finishEyedropper]);

  const capturePointer = (event) => {
    const target = viewportRef.current || event.currentTarget;
    if (!target?.setPointerCapture) return;
    try {
      target.setPointerCapture(event.pointerId);
    } catch (error) {
      // Ignore pointer capture errors.
    }
  };

  const getWorldPointFromEvent = (event) => {
    const viewportPoint = getViewportPoint(event, viewportRef);
    return viewportToWorld(viewportPoint, stageControls.pan, stageControls.zoom);
  };

  const screenToWorld = useCallback(
    (clientX, clientY) => {
      if (!viewportRef.current) return { x: 0, y: 0 };
      const rect = viewportRef.current.getBoundingClientRect();
      const sx = clientX - rect.left;
      const sy = clientY - rect.top;
      // stage transform: translate(panX, panY) scale(zoom)
      // inverse: (screen - pan) / zoom
      return {
        x: (sx - stageControls.pan.x) / stageControls.zoom,
        y: (sy - stageControls.pan.y) / stageControls.zoom,
      };
    },
    [stageControls.pan, stageControls.zoom],
  );

  const worldToElementLocal = useCallback((element, worldPoint) => {
    const cx = element.x + element.w / 2;
    const cy = element.y + element.h / 2;
    const dx = worldPoint.x - cx;
    const dy = worldPoint.y - cy;
    const angle = -((element.rotation || 0) * Math.PI) / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return {
      x: dx * cos - dy * sin + element.w / 2,
      y: dx * sin + dy * cos + element.h / 2,
    };
  }, []);

  const isLocalPointInside = useCallback((element, localPoint) => (
    localPoint.x >= 0
    && localPoint.y >= 0
    && localPoint.x <= element.w
    && localPoint.y <= element.h
  ), []);

  const { page: activePage, pageIndex } = useMemo(() => {
    if (!editorPages.length) {
      return { page: null, pageIndex: -1 };
    }
    const index = editorPages.findIndex((item) => item.id === activePageId);
    if (index >= 0) {
      return { page: editorPages[index], pageIndex: index };
    }
    return { page: editorPages[0], pageIndex: 0 };
  }, [activePageId, editorPages]);

  const realElements = activePage?.elements || [];
  const usingDemoElements = realElements.length === 0;

  useEffect(() => {
    if (!project) return;
    if (!usingDemoElements) {
      setDemoElements([]);
      demoSeededRef.current = false;
      return;
    }
    if (!demoSeededRef.current) {
      setDemoElements(createDemoElements(artboardWidth, artboardHeight));
      demoSeededRef.current = true;
    }
  }, [artboardHeight, artboardWidth, project, usingDemoElements]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      if (localSaveTimerRef.current) {
        clearTimeout(localSaveTimerRef.current);
      }
    };
  }, []);

  const buildPersistedSnapshot = useCallback((cache) => {
    if (!cache) return null;
    const { view, canvas, ...rest } = cache;
    return sanitizeDeep(rest);
  }, []);

  const flushSave = useCallback(async (reason = 'flush') => {
    if (!firebase || !franchiseeId || !projectId) return;
    if (saveInFlightRef.current) return;
    const snapshot = buildPersistedSnapshot(projectCacheRef.current);
    if (!snapshot) return;
    saveInFlightRef.current = true;
    saveDirtyRef.current = false;
    try {
      setSaveError('');
      setSaveDebug('');
      await updateProject(firebase, franchiseeId, projectId, snapshot);
      lastSaveAtRef.current = Date.now();
      setSaveStatus('Saved');
    } catch (error) {
      console.error('Failed to save studio project:', error);
      setSaveStatus('Saved');
      setSaveError('Save failed. Changes may not sync.');
      if (process.env.NODE_ENV !== 'production') {
        setSaveDebug(error?.message || 'Unknown error');
      }
    } finally {
      saveInFlightRef.current = false;
      if (saveDirtyRef.current) {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => flushSave('dirty'), 1200);
      }
    }
  }, [buildPersistedSnapshot, firebase, franchiseeId, projectId]);

  const applyPatchToCache = useCallback((patch) => {
    if (!patch) return projectCacheRef.current;
    const current = projectCacheRef.current || { id: projectId };
    let next = { ...current, ...patch };
    if (patch.pageSettings) {
      next.pageSettings = mergePageSettings(
        current.pageSettings || DEFAULT_PAGE_SETTINGS,
        patch.pageSettings,
      );
    }
    if (patch.palette) {
      next.palette = patch.palette;
    }
    if (patch.pages) {
      next.pages = patch.pages;
    }
    if (patch.activePageId !== undefined) {
      next.activePageId = patch.activePageId;
    }
    projectCacheRef.current = next;
    return next;
  }, [projectId]);

  const queueLocalCacheSave = useCallback(() => {
    if (!projectId) return;
    if (localSaveTimerRef.current) {
      clearTimeout(localSaveTimerRef.current);
    }
    localSaveTimerRef.current = setTimeout(() => {
      const snapshot = buildPersistedSnapshot(projectCacheRef.current);
      if (!snapshot) return;
      writeProjectCache(projectId, snapshot).catch((error) => {
        console.error('Failed to write local studio cache:', error);
      });
    }, 300);
  }, [buildPersistedSnapshot, projectId]);

  const queueSave = useCallback((patch, options = {}) => {
    const { immediate = false, reason = 'debounced' } = options;
    if (!firebase || !franchiseeId || !projectId) return;
    const sanitizedPatch = sanitizeDeep(patch);
    if (!sanitizedPatch || Object.keys(sanitizedPatch).length === 0) return;
    applyPatchToCache(sanitizedPatch);
    if (usingDemoElements) return;
    queueLocalCacheSave();
    saveDirtyRef.current = true;
    setSaveStatus('Saved');
    setSaveError('');
    if (immediate) {
      flushSave(reason);
      return;
    }
    const now = Date.now();
    const elapsed = now - lastSaveAtRef.current;
    if (!saveInFlightRef.current && elapsed >= 3000) {
      flushSave('throttle');
      return;
    }
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      flushSave(reason);
    }, 2500);
  }, [applyPatchToCache, firebase, flushSave, franchiseeId, projectId, usingDemoElements]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!projectId) return;
      const snapshot = buildPersistedSnapshot(projectCacheRef.current);
      if (!snapshot) return;
      writeProjectCache(projectId, snapshot).catch(() => {});
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [buildPersistedSnapshot, projectId]);

  const updatePageSettings = useCallback((patch, options = {}) => {
    const { commit = true } = options;
    setPageSettings((prev) => {
      const next = mergePageSettings(prev || DEFAULT_PAGE_SETTINGS, patch);
      setProject((current) => (current ? { ...current, pageSettings: next } : current));
      if (commit) {
        queueSave({ pageSettings: next });
      }
      return next;
    });
  }, [queueSave]);

  const updatePageSettingsPreview = useCallback(
    (patch) => updatePageSettings(patch, { commit: false }),
    [updatePageSettings],
  );

  function getBackgroundCoverScale(bg) {
    if (!bg) return 1;
    const naturalW = bg.naturalW || artboardWidth || 1;
    const naturalH = bg.naturalH || artboardHeight || 1;
    return Math.max((artboardWidth || 1) / naturalW, (artboardHeight || 1) / naturalH);
  }

  function clampBackgroundImage(bg) {
    if (!bg) return bg;
    const naturalW = bg.naturalW || artboardWidth || 1;
    const naturalH = bg.naturalH || artboardHeight || 1;
    const frame = { w: artboardWidth || 1, h: artboardHeight || 1 };
    const content = clampFrameContent(frame, {
      type: 'image',
      naturalW,
      naturalH,
      scale: bg.scale,
      offsetX: bg.offsetX,
      offsetY: bg.offsetY,
    });
    return {
      ...bg,
      naturalW,
      naturalH,
      scale: content?.scale ?? bg.scale,
      offsetX: content?.offsetX ?? bg.offsetX,
      offsetY: content?.offsetY ?? bg.offsetY,
    };
  }

  const applyBackgroundImageDraft = useCallback(
    (nextBg, { commit = false } = {}) => {
      const clamped = clampBackgroundImage(nextBg);
      pageBackgroundDraftRef.current = clamped;
      if (commit) {
        updatePageSettings({ backgroundImage: clamped });
        return;
      }
      updatePageSettingsPreview({ backgroundImage: clamped });
      if (isBackgroundRepositioning) return;
      if (pageBackgroundCommitRef.current) {
        clearTimeout(pageBackgroundCommitRef.current);
      }
      pageBackgroundCommitRef.current = setTimeout(() => {
        if (pageBackgroundDraftRef.current) {
          updatePageSettings({ backgroundImage: pageBackgroundDraftRef.current });
        }
      }, 300);
    },
    [clampBackgroundImage, isBackgroundRepositioning, updatePageSettings, updatePageSettingsPreview],
  );

  const downloadUrlCacheRef = useRef(new Map());
  const downloadUrlInflightRef = useRef(new Map());
  const objectUrlCacheRef = useRef(new Map());
  const getAssetDownloadUrl = useCallback(
    async (assetId) => {
      if (!firebase || !assetId) return '';
      const cached = downloadUrlCacheRef.current.get(assetId);
      if (cached) return cached;
      const inflight = downloadUrlInflightRef.current.get(assetId);
      if (inflight) return inflight;
      const promise = driveDownloadBlobUrl(firebase, assetId, { rootType: 'studio', forceRefresh: true })
        .then((url) => {
          if (url) {
            const previous = objectUrlCacheRef.current.get(assetId);
            if (previous && previous !== url) {
              URL.revokeObjectURL(previous);
            }
            objectUrlCacheRef.current.set(assetId, url);
            downloadUrlCacheRef.current.set(assetId, url);
          }
          return url || '';
        })
        .finally(() => {
          downloadUrlInflightRef.current.delete(assetId);
        });
      downloadUrlInflightRef.current.set(assetId, promise);
      return promise;
    },
    [firebase],
  );

  useEffect(() => {
    return () => {
      objectUrlCacheRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      objectUrlCacheRef.current.clear();
    };
  }, []);

  const handleSelectBackgroundImage = useCallback(
    async (asset) => {
      if (!asset) return;
      const src = asset.downloadUrl || (await getAssetDownloadUrl(asset.id));
      if (!src) return;
      const naturalW = asset.width || 1200;
      const naturalH = asset.height || 1200;
      const coverScale = Math.max(artboardWidth / naturalW, artboardHeight / naturalH);
      const imgW = naturalW * coverScale;
      const imgH = naturalH * coverScale;
      const next = clampBackgroundImage({
        enabled: true,
        assetId: asset.id,
        src,
        naturalW,
        naturalH,
        scale: coverScale,
        offsetX: (artboardWidth - imgW) / 2,
        offsetY: (artboardHeight - imgH) / 2,
        opacity: 100,
        userScaled: false,
        userMoved: false,
      });
      updatePageSettings({ backgroundImage: next });
    },
    [artboardHeight, artboardWidth, clampBackgroundImage, getAssetDownloadUrl, updatePageSettings],
  );

  const handleClearBackgroundImage = useCallback(() => {
    updatePageSettings({
      backgroundImage: {
        enabled: false,
        assetId: '',
        src: '',
        naturalW: 1200,
        naturalH: 1200,
        scale: 1,
        offsetX: 0,
        offsetY: 0,
        opacity: 100,
        userScaled: false,
        userMoved: false,
      },
    });
    setIsBackgroundRepositioning(false);
  }, [updatePageSettings]);

  const handleToggleBackgroundReposition = useCallback(() => {
    if (!backgroundImageEnabled) return;
    setIsBackgroundRepositioning((prev) => {
      const next = !prev;
      if (next) {
        const currentBg = clampBackgroundImage(backgroundImage);
        pageBackgroundOriginalRef.current = currentBg;
        pageBackgroundDraftRef.current = currentBg;
        updatePageSettingsPreview({ backgroundImage: currentBg });
      } else if (pageBackgroundDraftRef.current) {
        updatePageSettings({ backgroundImage: pageBackgroundDraftRef.current });
      }
      return next;
    });
  }, [backgroundImage, backgroundImageEnabled, clampBackgroundImage, updatePageSettings, updatePageSettingsPreview]);

  const handleCommitBackgroundReposition = useCallback(() => {
    if (!pageBackgroundDraftRef.current) return;
    updatePageSettings({ backgroundImage: pageBackgroundDraftRef.current });
    setIsBackgroundRepositioning(false);
  }, [updatePageSettings]);

  const handleCancelBackgroundReposition = useCallback(() => {
    if (pageBackgroundOriginalRef.current) {
      updatePageSettings({ backgroundImage: pageBackgroundOriginalRef.current });
    }
    setIsBackgroundRepositioning(false);
  }, [updatePageSettings]);

  const handleResetBackgroundReposition = useCallback(() => {
    if (!backgroundImageEnabled || !backgroundImage) return;
    const naturalW = backgroundImage.naturalW || artboardWidth || 1;
    const naturalH = backgroundImage.naturalH || artboardHeight || 1;
    const coverScale = Math.max(artboardWidth / naturalW, artboardHeight / naturalH);
    const imgW = naturalW * coverScale;
    const imgH = naturalH * coverScale;
    const next = clampBackgroundImage({
      ...backgroundImage,
      scale: coverScale,
      offsetX: (artboardWidth - imgW) / 2,
      offsetY: (artboardHeight - imgH) / 2,
      userScaled: false,
      userMoved: false,
    });
    applyBackgroundImageDraft(next, { commit: false });
  }, [
    applyBackgroundImageDraft,
    artboardHeight,
    artboardWidth,
    backgroundImage,
    backgroundImageEnabled,
    clampBackgroundImage,
  ]);

  useEffect(() => {
    if (!backgroundImageEnabled || !backgroundImage?.src) return;
    const naturalW = backgroundImage.naturalW || 0;
    const naturalH = backgroundImage.naturalH || 0;
    if (naturalW > 0 && naturalH > 0) return;
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      const resolvedW = img.naturalWidth || artboardWidth || 1;
      const resolvedH = img.naturalHeight || artboardHeight || 1;
      const oldCoverScale = getBackgroundCoverScale(backgroundImage);
      const nextCoverScale = computeCoverScale(artboardWidth, artboardHeight, resolvedW, resolvedH);
      const baseScale = Number(backgroundImage.scale) || oldCoverScale;
      const shouldAutoFit = !backgroundImage.userScaled && !backgroundImage.userMoved;
      const isNearOldCover = Math.abs(baseScale - oldCoverScale) < 0.02;
      const nextScale = shouldAutoFit && isNearOldCover ? nextCoverScale : baseScale;
      const imgW = resolvedW * nextScale;
      const imgH = resolvedH * nextScale;
      const next = clampBackgroundImage({
        ...backgroundImage,
        naturalW: resolvedW,
        naturalH: resolvedH,
        scale: nextScale,
        offsetX: shouldAutoFit && isNearOldCover ? (artboardWidth - imgW) / 2 : backgroundImage.offsetX,
        offsetY: shouldAutoFit && isNearOldCover ? (artboardHeight - imgH) / 2 : backgroundImage.offsetY,
      });
      updatePageSettings({ backgroundImage: next });
    };
    img.src = backgroundImage.src;
    return () => {
      cancelled = true;
    };
  }, [
    artboardHeight,
    artboardWidth,
    backgroundImage,
    backgroundImageEnabled,
    clampBackgroundImage,
    getBackgroundCoverScale,
    artboardWidth,
    artboardHeight,
    updatePageSettings,
  ]);


  const handleUsePaint = useCallback((paint) => {
    if (!paint) return;
    setPalette((prev) => {
      const next = addRecentPaint(prev, paint);
      if (!usingDemoElements) {
        queueSave({ palette: next });
        setProject((current) => (current ? { ...current, palette: next } : current));
      }
      return next;
    });
  }, [queueSave, usingDemoElements]);

  const persistElements = useCallback((nextElements) => {
    if (!activePage || pageIndex < 0) return;
    const nextPages = editorPages.map((page, index) => (
      index === pageIndex ? { ...page, elements: nextElements } : page
    ));
    setEditorPages(nextPages);
    setProject((prev) => (prev ? { ...prev, pages: nextPages, activePageId } : prev));
    queueSave({ pages: nextPages, activePageId });
  }, [activePage, activePageId, editorPages, pageIndex, queueSave]);

  const setElementsLocal = useCallback((nextElements) => {
    if (!activePage || pageIndex < 0) return;
    const nextPages = editorPages.map((page, index) => (
      index === pageIndex ? { ...page, elements: nextElements } : page
    ));
    setEditorPages(nextPages);
    setProject((prev) => (prev ? { ...prev, pages: nextPages, activePageId } : prev));
    applyPatchToCache({ pages: nextPages, activePageId });
  }, [activePage, activePageId, applyPatchToCache, editorPages, pageIndex]);

  const updateElements = useCallback((nextElements, options = {}) => {
    const { forcePersist = false, commit = true } = options;
    if (usingDemoElements && !forcePersist) {
      setDemoElements(nextElements);
      return;
    }
    if (commit) {
      persistElements(nextElements);
    } else {
      setElementsLocal(nextElements);
    }
  }, [persistElements, setElementsLocal, usingDemoElements]);

  const commitElementsToFirestore = useCallback((nextElements, reason) => {
    if (usingDemoElements) {
      setDemoElements(nextElements);
      return;
    }
    if (!activePage || pageIndex < 0) return;
    const nextPages = editorPages.map((page, index) => (
      index === pageIndex ? { ...page, elements: nextElements } : page
    ));
    setEditorPages(nextPages);
    setProject((prev) => (prev ? { ...prev, pages: nextPages, activePageId } : prev));
    queueSave(
      { pages: nextPages, activePageId },
      { immediate: true, reason: reason || 'commit-elements' },
    );
  }, [
    activePage,
    activePageId,
    editorPages,
    pageIndex,
    projectId,
    queueSave,
    usingDemoElements,
  ]);

  const textEditCommit = useDebouncedCommit((nextElements, reason) => {
    commitElementsToFirestore(nextElements, reason || 'text-edit');
  }, 700);

  const renderedElements = usingDemoElements ? demoElements : realElements;
  const elementsRef = useRef(renderedElements);

  useEffect(() => {
    elementsRef.current = renderedElements;
  }, [renderedElements]);

  useEffect(() => {
    if (!selectedIds.length) return;
    const valid = selectedIds.filter((id) => renderedElements.find((el) => el.id === id));
    if (valid.length !== selectedIds.length) {
      setSelectedIds(valid);
    }
  }, [renderedElements, selectedIds]);

  const layeredElements = useMemo(() => (
    [...renderedElements].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0))
  ), [renderedElements]);

  const getTopFrameAtWorldPoint = useCallback((worldPoint) => (
    layeredElements
      .filter((el) => el.type === 'frame')
      .find((frame) => {
        const local = worldToElementLocal(frame, worldPoint);
        return isLocalPointInside(frame, local);
      }) || null
  ), [isLocalPointInside, layeredElements, worldToElementLocal]);

  const getFrameLocalPointFromEvent = useCallback((frame, event) => {
    const worldPoint = getWorldPointFromEvent(event);
    return worldToElementLocal(frame, worldPoint);
  }, [worldToElementLocal]);

  const selectedElements = useMemo(
    () => renderedElements.filter((element) => selectedIds.includes(element.id)),
    [renderedElements, selectedIds],
  );

  useEffect(() => {
    if (!project) return;
    const nextDocumentColors = collectDocumentColors(renderedElements, pageSettings);
    setPalette((prev) => {
      const normalized = normalizePalette({
        ...prev,
        documentColors: nextDocumentColors,
      });
      const prevColors = prev?.documentColors || [];
      const isSame =
        prevColors.length === normalized.documentColors.length
        && prevColors.every((color, index) => color === normalized.documentColors[index]);
      if (!isSame && !usingDemoElements) {
        queueSave({ palette: normalized });
        setProject((current) => (current ? { ...current, palette: normalized } : current));
      }
      return isSame ? prev : normalized;
    });
  }, [pageSettings, project, queueSave, renderedElements, usingDemoElements]);

  useEffect(() => {
    let active = true;
    const target =
      selectedElements.length === 1 && selectedElements[0].type === 'image'
        ? selectedElements[0]
        : null;
    if (!target?.src) {
      setPhotoColors([]);
      return () => {
        active = false;
      };
    }
    extractPhotoColors(target.src)
      .then((colors) => {
        if (active) setPhotoColors(colors);
      })
      .catch(() => {
        if (active) setPhotoColors([]);
      });
    return () => {
      active = false;
    };
  }, [selectedElements]);

  const movableSelectedElements = useMemo(
    () => selectedElements.filter((element) => !element.locked),
    [selectedElements],
  );

  const selectedElement =
    selectedIds.length === 1
      ? renderedElements.find((element) => element.id === selectedIds[0]) || null
      : null;
  const imageToolTarget = useMemo(() => {
    if (!imageToolPanel?.open || !imageToolPanel.targetId) return null;
    return renderedElements.find((el) => el.id === imageToolPanel.targetId) || null;
  }, [imageToolPanel, renderedElements]);
  const cropTarget =
    imageToolPanel?.open && imageToolPanel.tool === 'crop' ? imageToolTarget : null;
  const cropRect =
    imageToolPanel?.open && imageToolPanel.tool === 'crop' ? cropDraft : null;
  const frameCropTarget = useMemo(() => {
    if (!frameCropState?.active || !frameCropState.frameId) return null;
    const match = renderedElements.find(
      (el) => el.id === frameCropState.frameId && el.type === 'frame',
    );
    return match || null;
  }, [frameCropState, renderedElements]);
  const frameCropDraftContent =
    frameCropTarget && frameCropState?.active ? frameCropState.draftContent : null;
  const isFrameCropBlocking = Boolean(frameCropTarget);
  const isImageToolBlocking =
    (imageToolPanel?.open && ['crop', 'eraser'].includes(imageToolPanel.tool))
    || isFrameCropBlocking;
  const eraserMode =
    imageToolPanel?.open && imageToolPanel.tool === 'eraser'
      ? imageToolTarget?.eraser?.mode || 'pixel'
      : null;

  useEffect(() => {
    if (!frameCropState.active) return;
    if (frameCropTarget) return;
    setFrameCropState({ active: false, frameId: null, draftContent: null });
  }, [frameCropState.active, frameCropTarget]);

  useEffect(() => {
    if (frameCropState.active) {
      isInteractingRef.current = true;
    }
  }, [frameCropState.active]);

  const assetsById = useMemo(
    () => new Map((assets || []).map((asset) => [asset.id, asset])),
    [assets],
  );
  const latestImageAsset = useMemo(() => {
    if (!assets || !assets.length) return null;
    return assets.find((asset) => asset.type !== 'pdf') || null;
  }, [assets]);

  const refreshedAssetIdsRef = useRef(new Set());

  useEffect(() => {
    if (usingDemoElements || frameCropState.active) return;

    let didChange = false;
    const missingAssetIds = new Set();
    const nextElements = renderedElements.map((element) => {
      if (element.type === 'image') {
        const asset = element.assetId ? assetsById.get(element.assetId) : null;
        if (asset?.downloadUrl && asset.downloadUrl !== element.src) {
          didChange = true;
          return {
            ...element,
            src: asset.downloadUrl,
            naturalW: asset.width || element.naturalW,
            naturalH: asset.height || element.naturalH,
          };
        }
        if (element.assetId && !asset?.downloadUrl) {
          missingAssetIds.add(element.assetId);
        }
        return element;
      }
      if (element.type === 'frame' && element.content?.type === 'image') {
        const asset = element.content.assetId
          ? assetsById.get(element.content.assetId)
          : null;
        if (asset?.downloadUrl && asset.downloadUrl !== element.content.src) {
          didChange = true;
          return {
            ...element,
            content: {
              ...element.content,
              src: asset.downloadUrl,
              naturalW: asset.width || element.content.naturalW,
              naturalH: asset.height || element.content.naturalH,
            },
          };
        }
        if (element.content.assetId && !asset?.downloadUrl) {
          missingAssetIds.add(element.content.assetId);
        }
        return element;
      }
      return element;
    });

    if (didChange) {
      setElementsLocal(nextElements);
      queueLocalCacheSave();
    }

    if (backgroundImage?.assetId) {
      const asset = assetsById.get(backgroundImage.assetId);
      if (asset?.downloadUrl && asset.downloadUrl !== backgroundImage.src) {
        const nextBackground = {
          ...backgroundImage,
          src: asset.downloadUrl,
          naturalW: asset.width || backgroundImage.naturalW,
          naturalH: asset.height || backgroundImage.naturalH,
        };
        updatePageSettingsPreview({ backgroundImage: nextBackground });
        applyPatchToCache({ pageSettings: mergePageSettings(pageSettings, { backgroundImage: nextBackground }) });
        queueLocalCacheSave();
      }
      if (!asset?.downloadUrl) {
        missingAssetIds.add(backgroundImage.assetId);
      }
    }

    if (!firebase || missingAssetIds.size === 0) return;

    const pendingIds = Array.from(missingAssetIds).filter(
      (assetId) => assetId && !refreshedAssetIdsRef.current.has(assetId),
    );
    if (!pendingIds.length) return;

    let cancelled = false;
    const refreshMissing = async () => {
      const refreshedUrls = new Map();
      for (const assetId of pendingIds) {
        try {
          const url = await getAssetDownloadUrl(assetId);
          if (cancelled) return;
          if (url) {
            refreshedUrls.set(assetId, url);
            refreshedAssetIdsRef.current.add(assetId);
          }
        } catch (error) {
          console.error('Failed to refresh Drive download URL:', assetId, error);
        }
      }

      if (cancelled || refreshedUrls.size === 0) return;

      let didRefresh = false;
      const refreshedElements = nextElements.map((element) => {
        if (element.type === 'image' && element.assetId) {
          const freshUrl = refreshedUrls.get(element.assetId);
          if (freshUrl && freshUrl !== element.src) {
            didRefresh = true;
            return {
              ...element,
              src: freshUrl,
            };
          }
        }
        if (element.type === 'frame' && element.content?.type === 'image') {
          const freshUrl = refreshedUrls.get(element.content.assetId);
          if (freshUrl && freshUrl !== element.content.src) {
            didRefresh = true;
            return {
              ...element,
              content: {
                ...element.content,
                src: freshUrl,
              },
            };
          }
        }
        return element;
      });

      if (didRefresh) {
        setElementsLocal(refreshedElements);
        queueLocalCacheSave();
      }

      if (backgroundImage?.assetId) {
        const freshUrl = refreshedUrls.get(backgroundImage.assetId);
        if (freshUrl && freshUrl !== backgroundImage.src) {
          const nextBackground = {
            ...backgroundImage,
            src: freshUrl,
          };
          updatePageSettingsPreview({ backgroundImage: nextBackground });
          applyPatchToCache({ pageSettings: mergePageSettings(pageSettings, { backgroundImage: nextBackground }) });
          queueLocalCacheSave();
        }
      }
    };

    refreshMissing();
    return () => {
      cancelled = true;
    };
  }, [
    applyPatchToCache,
    assetsById,
    backgroundImage,
    frameCropState.active,
    firebase,
    getAssetDownloadUrl,
    pageSettings,
    queueLocalCacheSave,
    renderedElements,
    setElementsLocal,
    updatePageSettingsPreview,
    usingDemoElements,
  ]);

  const canvasElements = useMemo(() => {
    let nextElements = renderedElements;
    if (cropTarget && cropRect) {
      nextElements = nextElements.map((el) =>
        el.id === cropTarget.id
          ? { ...el, crop: { ...cropRect, enabled: true } }
          : el,
      );
    }
    if (frameCropTarget && frameCropDraftContent) {
      nextElements = nextElements.map((el) =>
        el.id === frameCropTarget.id
          ? { ...el, content: frameCropDraftContent }
          : el,
      );
    }
    return nextElements;
  }, [cropRect, cropTarget, frameCropDraftContent, frameCropTarget, renderedElements]);

  const selectionBoundsWorld = useMemo(() => {
    const source = movableSelectedElements.length ? movableSelectedElements : selectedElements;
    return source.length ? getBoundsFromElements(source) : null;
  }, [movableSelectedElements, selectedElements]);

  const selectionBoundsScreen = useMemo(() => {
    if (!selectionBoundsWorld) return null;
    const topLeft = worldToViewport(
      { x: selectionBoundsWorld.x, y: selectionBoundsWorld.y },
      stageControls.pan,
      stageControls.zoom,
    );
    return {
      x: topLeft.x,
      y: topLeft.y,
      width: selectionBoundsWorld.w * stageControls.zoom,
      height: selectionBoundsWorld.h * stageControls.zoom,
    };
  }, [selectionBoundsWorld, stageControls.pan, stageControls.zoom]);

  const transformBounds = editingTextId || isImageToolBlocking ? null : selectionBoundsScreen;

  const handleDelete = useCallback(() => {
    if (!selectedIds.length) return;
    const nextElements = renderedElements.filter((element) => !selectedIds.includes(element.id));
    updateElements(nextElements);
    setSelectedIds([]);
  }, [renderedElements, selectedIds, updateElements]);

  const handleDuplicate = useCallback(() => {
    if (!selectedIds.length) return;
    const selected = renderedElements.filter((element) => selectedIds.includes(element.id));
    const clones = cloneElementsWithOffset(selected, 10);
    updateElements([...renderedElements, ...clones]);
    setSelectedIds(clones.map((clone) => clone.id));
  }, [renderedElements, selectedIds, updateElements]);

  const handleToggleLock = () => {
    if (!selectedIds.length) return;
    const shouldLock = selectedElements.some((element) => !element.locked);
    const nextElements = renderedElements.map((element) => (
      selectedIds.includes(element.id)
        ? { ...element, locked: shouldLock }
        : element
    ));
    updateElements(nextElements);
  };

  const adjustZIndex = (delta) => {
    if (!selectedIds.length) return;
    const nextElements = renderedElements.map((element) => {
      if (!selectedIds.includes(element.id)) return element;
      return {
        ...element,
        zIndex: (element.zIndex || 0) + delta,
      };
    });
    updateElements(nextElements);
  };

  const bringToFront = () => {
    if (!selectedIds.length) return;
    const maxZ = Math.max(...renderedElements.map((element) => element.zIndex || 0), 0);
    const nextElements = renderedElements.map((element) => (
      selectedIds.includes(element.id)
        ? { ...element, zIndex: maxZ + 1 }
        : element
    ));
    updateElements(nextElements);
  };

  const sendToBack = () => {
    if (!selectedIds.length) return;
    const minZ = Math.min(...renderedElements.map((element) => element.zIndex || 0), 0);
    const nextElements = renderedElements.map((element) => (
      selectedIds.includes(element.id)
        ? { ...element, zIndex: minZ - 1 }
        : element
    ));
    updateElements(nextElements);
  };

  const handleAddText = (kind) => {
    const elementId = `text-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const defaults = {
      heading: { text: 'Heading', fontSize: 48, fontWeight: 600, lineHeight: 1.1 },
      subheading: { text: 'Subheading', fontSize: 30, fontWeight: 500, lineHeight: 1.2 },
      body: { text: 'Body text', fontSize: 20, fontWeight: 400, lineHeight: 1.4 },
    };
    const config = defaults[kind] || defaults.body;
    const measure = measureTextBox({
      text: config.text,
      fontFamily: 'Inter, sans-serif',
      fontSize: config.fontSize,
      fontWeight: config.fontWeight,
      fontStyle: 'normal',
      lineHeight: config.lineHeight,
      letterSpacing: 0,
      autoWidth: true,
      paddingX: 0,
      paddingY: 0,
      minW: 40,
    });
    const width = Math.round(measure.tightW);
    const height = Math.round(measure.tightH);
    const nextElement = {
      id: elementId,
      type: 'text',
      x: Math.round((artboardWidth - width) / 2),
      y: Math.round((artboardHeight - height) / 2),
      w: Math.round(width),
      h: Math.round(height),
      rotation: 0,
      opacity: 1,
      locked: false,
      zIndex: renderedElements.reduce((acc, element) => Math.max(acc, element.zIndex || 0), 0) + 1,
      text: config.text,
      fontFamily: 'Inter, sans-serif',
      fontSize: config.fontSize,
      fontWeight: config.fontWeight,
      fontStyle: 'normal',
      underline: false,
      color: '#111',
      align: 'left',
      lineHeight: config.lineHeight,
      letterSpacing: 0,
      paddingX: 0,
      paddingY: 0,
      autoWidth: true,
      autoHeight: true,
      minW: 40,
    };

    const baseElements = usingDemoElements ? [] : renderedElements;
    updateElements([...baseElements, nextElement], { forcePersist: true });
    setSelectedIds([elementId]);
  };

  const getCanvasCenterWorld = () => {
    if (!viewportRef.current) {
      return { x: artboardWidth / 2, y: artboardHeight / 2 };
    }
    const rect = viewportRef.current.getBoundingClientRect();
    const centerPoint = { x: rect.width / 2, y: rect.height / 2 };
    return viewportToWorld(centerPoint, stageControls.pan, stageControls.zoom);
  };

  const handleInsertElement = useCallback((item) => {
    if (!item) return;
    const baseElements = usingDemoElements ? [] : renderedElements;
    const result = insertCatalogItem({
      catalogItem: item,
      existingElements: baseElements,
      center: getCanvasCenterWorld(),
      artboardWidth,
      artboardHeight,
    });
    if (!result) return;
    updateElements(result.elements, { forcePersist: true });
    if (result.selectedIds?.length) {
      setSelectedIds(result.selectedIds);
    }
  }, [
    artboardHeight,
    artboardWidth,
    getCanvasCenterWorld,
    renderedElements,
    updateElements,
    usingDemoElements,
  ]);

  const getElementViewportBounds = (element) => {
    if (!viewportRef.current || !element) return null;
    const rect = viewportRef.current.getBoundingClientRect();
    const topLeft = worldToViewport(
      { x: element.x, y: element.y },
      stageControls.pan,
      stageControls.zoom,
    );
    return {
      left: rect.left + topLeft.x,
      top: rect.top + topLeft.y,
      width: element.w * stageControls.zoom,
      height: element.h * stageControls.zoom,
    };
  };

  const computeAssetSize = (asset) => {
    const maxW = artboardWidth * 0.6;
    const maxH = artboardHeight * 0.6;
    const naturalW = Number(asset.width) || 0;
    const naturalH = Number(asset.height) || 0;

    if (naturalW > 0 && naturalH > 0) {
      let scale = Math.min(maxW / naturalW, maxH / naturalH, 1);
      let w = naturalW * scale;
      let h = naturalH * scale;
      const minDim = Math.min(w, h);
      if (minDim < 120) {
        const scaleUp = 120 / minDim;
        w = Math.min(w * scaleUp, maxW);
        h = Math.min(h * scaleUp, maxH);
      }
      return { w: Math.round(w), h: Math.round(h) };
    }

    const fallbackW = Math.min(maxW, 400);
    const fallbackH = Math.min(maxH, 300);
    return { w: Math.round(fallbackW), h: Math.round(fallbackH) };
  };

  const placeAssetOnCanvas = useCallback(
    async (asset, event) => {
      if (!asset || asset.type === 'pdf') return;
      const src = asset.downloadUrl || (await getAssetDownloadUrl(asset.id));
      if (!src) return;
      const point = event
        ? viewportToWorld(getViewportPoint(event, viewportRef), stageControls.pan, stageControls.zoom)
        : getCanvasCenterWorld();
      const { w, h } = computeAssetSize(asset);
      let nextX = point.x - w / 2;
      let nextY = point.y - h / 2;
      const maxX = Math.max(0, artboardWidth - w);
      const maxY = Math.max(0, artboardHeight - h);
      nextX = Math.min(Math.max(nextX, 0), maxX);
      nextY = Math.min(Math.max(nextY, 0), maxY);

      const baseElements = usingDemoElements ? [] : renderedElements;
      const maxZ = Math.max(...baseElements.map((el) => el.zIndex || 0), 0);
      const elementId = createElementId();
      const nextElement = {
        id: elementId,
        type: 'image',
        src,
        assetId: asset.id,
        x: nextX,
        y: nextY,
        w,
        h,
        rotation: 0,
        opacity: 1,
        locked: false,
        zIndex: maxZ + 1,
        fit: 'cover',
        flipX: false,
        flipY: false,
        radius: 0,
        crop: {
          enabled: false,
          x: 0,
          y: 0,
          w: 1,
          h: 1,
        },
        adjust: {
          brightness: 1,
          contrast: 1,
          saturation: 1,
          warmth: 0,
          blur: 0,
          temperature: 0,
          tint: 0,
          highlights: 0,
          shadows: 0,
          whites: 0,
          blacks: 0,
          vibrance: 0,
          invert: false,
          sharpness: 0,
          clarity: 0,
          vignette: 0,
        },
        filterPreset: 'none',
        eraser: {
          enabled: false,
          mode: 'pixel',
          brushSize: 24,
          hardness: 0.8,
          opacity: 1,
          strokes: [],
          magic: {
            enabled: false,
            tolerance: 25,
            contiguous: true,
            feather: 2,
            expand: 0,
            opMode: 'add',
            ops: [],
          },
        },
        shadow: {
          enabled: false,
          x: 0,
          y: 8,
          blur: 20,
          color: '#000000',
          opacity: 0.25,
        },
      };
      const nextElements = [...baseElements, nextElement];
      updateElements(nextElements, { forcePersist: true, commit: true });
      setSelectedIds([elementId]);
    },
    [
      artboardHeight,
      artboardWidth,
      getAssetDownloadUrl,
      firebase,
      franchiseeId,
      renderedElements,
      stageControls.pan,
      stageControls.zoom,
      updateElements,
      usingDemoElements,
    ],
  );

  const handlePlaceAsset = useCallback(
    (asset) => {
      placeAssetOnCanvas(asset);
    },
    [placeAssetOnCanvas],
  );

  const fillFrameWithImage = useCallback(
    async (frame, asset) => {
      if (!frame || !asset || asset.type === 'pdf') return false;
      const src = asset.downloadUrl || (await getAssetDownloadUrl(asset.id));
      if (!src) return false;
      const nextContent = createFrameContentFromAsset(frame, { ...asset, downloadUrl: src });
      if (!nextContent) return false;
      const nextElements = renderedElements.map((el) =>
        el.id === frame.id
          ? { ...el, content: nextContent }
          : el,
      );
      updateElements(nextElements, { forcePersist: true, commit: true });
      setSelectedIds([frame.id]);
      return true;
    },
    [getAssetDownloadUrl, renderedElements, updateElements],
  );

  const handleAssetDrop = useCallback(
    async (assetId, event) => {
      const asset = assetsById.get(assetId);
      if (!asset) return;
      const worldPoint = getWorldPointFromEvent(event);
      const frameTarget = getTopFrameAtWorldPoint(worldPoint);
      const isImageAsset = asset.type !== 'pdf';
      if (frameTarget && isImageAsset) {
        const filled = await fillFrameWithImage(frameTarget, asset);
        if (filled) return;
      }
      await placeAssetOnCanvas(asset, event);
    },
    [
      assetsById,
      fillFrameWithImage,
      getWorldPointFromEvent,
      getTopFrameAtWorldPoint,
      placeAssetOnCanvas,
    ],
  );

  const handleBeginAssetDrag = useCallback((asset, event) => {
    if (!asset?.id || !event) return;
    if (isSpacePressedRef.current) return;
    dragAssetRef.current = {
      active: true,
      armed: false,
      type: 'asset-image',
      payload: {
        assetId: asset.id,
        src: asset.downloadUrl,
        naturalW: Number(asset.width) || 0,
        naturalH: Number(asset.height) || 0,
      },
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      clientX: event.clientX,
      clientY: event.clientY,
      hoveredFrameId: null,
    };
    setDragUI((prev) => ({
      ...prev,
      active: true,
      armed: false,
      clientX: event.clientX,
      clientY: event.clientY,
      hoveredFrameId: null,
    }));
    if (debugEnabled) {
      setDragDebug((prev) => ({
        ...prev,
        lastDropAction: 'none',
        lastError: '',
      }));
      console.debug('[studio-drag] start', {
        assetId: asset.id,
        clientX: event.clientX,
        clientY: event.clientY,
      });
    }
  }, [debugEnabled]);

  const handleEndAssetDrag = useCallback(() => {
    dragAssetRef.current = {
      active: false,
      armed: false,
      type: null,
      payload: null,
      pointerId: null,
      startClientX: 0,
      startClientY: 0,
      clientX: 0,
      clientY: 0,
      hoveredFrameId: null,
    };
    setDragUI({
      active: false,
      armed: false,
      clientX: 0,
      clientY: 0,
      hoveredFrameId: null,
    });
  }, []);

  const updateDragUiRaf = useCallback((next) => {
    if (dragRafRef.current) return;
    dragRafRef.current = window.requestAnimationFrame(() => {
      dragRafRef.current = null;
      setDragUI((prev) => ({ ...prev, ...next }));
    });
  }, []);

  useEffect(() => {
    if (!dragUI.active) return undefined;
    const handlePointerMove = (event) => {
      const drag = dragAssetRef.current;
      if (!drag.active || event.pointerId !== drag.pointerId) return;
      const dx = event.clientX - drag.startClientX;
      const dy = event.clientY - drag.startClientY;
      if (!drag.armed && Math.hypot(dx, dy) < 6) {
        return;
      }
      drag.armed = true;
      drag.clientX = event.clientX;
      drag.clientY = event.clientY;
      const world = screenToWorld(event.clientX, event.clientY);
      const frame = getTopFrameAtWorldPoint(world);
      drag.hoveredFrameId = frame?.id || null;
      updateDragUiRaf({
        active: true,
        armed: true,
        clientX: event.clientX,
        clientY: event.clientY,
        worldX: world.x,
        worldY: world.y,
        hoveredFrameId: drag.hoveredFrameId,
      });
      if (debugEnabled) {
        const rect = viewportRef.current?.getBoundingClientRect() || null;
        setDragDebug((prev) => ({
          ...prev,
          canvasRect: rect
            ? { left: rect.left, top: rect.top, width: rect.width, height: rect.height }
            : null,
        }));
        const now = Date.now();
        if (now - dragLogRef.current > 200) {
          dragLogRef.current = now;
          console.debug('[studio-drag] move', {
            clientX: event.clientX,
            clientY: event.clientY,
            worldX: Math.round(world.x),
            worldY: Math.round(world.y),
            hovered: drag.hoveredFrameId,
          });
        }
      }
      event.preventDefault();
    };
    const handlePointerUp = (event) => {
      const drag = dragAssetRef.current;
      if (!drag.active || event.pointerId !== drag.pointerId) return;
      let lastDropAction = 'none';
      let lastError = '';
      try {
        const payload = drag.payload;
        const asset = payload?.assetId ? assetsById.get(payload.assetId) : null;
        if (drag.armed && asset) {
          const world = screenToWorld(event.clientX, event.clientY);
          const frame = getTopFrameAtWorldPoint(world);
          if (frame) {
            fillFrameWithImage(frame, asset);
            lastDropAction = `fillFrame:${frame.id}`;
          }
        }
      } catch (error) {
        lastError = error?.message || 'Unknown drop error';
        if (debugEnabled) console.error('Drag drop error:', error);
      }
      if (debugEnabled) {
        setDragDebug((prev) => ({
          ...prev,
          lastDropAction,
          lastError,
        }));
        console.debug('[studio-drag] end', { lastDropAction, lastError });
      }
      handleEndAssetDrag();
    };
    const handlePointerCancel = (event) => {
      const drag = dragAssetRef.current;
      if (!drag.active || event.pointerId !== drag.pointerId) return;
      if (debugEnabled) {
        setDragDebug((prev) => ({
          ...prev,
          lastDropAction: 'cancelled',
        }));
      }
      handleEndAssetDrag();
    };
    const handleBlur = () => {
      if (!dragAssetRef.current.active) return;
      if (debugEnabled) {
        setDragDebug((prev) => ({
          ...prev,
          lastDropAction: 'cancelled',
        }));
      }
      handleEndAssetDrag();
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        handleBlur();
      }
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', handlePointerMove, { passive: false, capture: true });
    window.addEventListener('pointerup', handlePointerUp, { capture: true });
    window.addEventListener('pointercancel', handlePointerCancel, { capture: true });
    window.addEventListener('blur', handleBlur, { capture: true });
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', handlePointerMove, { capture: true });
      window.removeEventListener('pointerup', handlePointerUp, { capture: true });
      window.removeEventListener('pointercancel', handlePointerCancel, { capture: true });
      window.removeEventListener('blur', handleBlur, { capture: true });
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [
    assetsById,
    debugEnabled,
    fillFrameWithImage,
    getTopFrameAtWorldPoint,
    handleEndAssetDrag,
    screenToWorld,
    updateDragUiRaf,
    dragUI.active,
  ]);

  const handleOpenImageTool = useCallback(
    (tool) => {
      if (!selectedElement || selectedElement.type !== 'image') return;
      setImageToolPanel({ open: true, tool, targetId: selectedElement.id });
      if (tool === 'crop') {
        const initialCrop = selectedElement.crop?.enabled
          ? selectedElement.crop
          : { x: 0, y: 0, w: 1, h: 1 };
        setCropDraft({ ...initialCrop });
      }
      if (tool === 'eraser') {
        setActiveEraserStroke(null);
      }
    },
    [selectedElement],
  );

  const handleDebugFillFrame = useCallback((frameId) => {
    if (!debugEnabled || !frameId || !latestImageAsset) return;
    const frame = renderedElements.find((el) => el.id === frameId && el.type === 'frame');
    if (!frame) return;
    fillFrameWithImage(frame, latestImageAsset);
  }, [debugEnabled, fillFrameWithImage, latestImageAsset, renderedElements]);

  const handleSelectFrameAsset = useCallback((frameId, asset) => {
    if (!frameId || !asset) return;
    const frame = renderedElements.find((el) => el.id === frameId && el.type === 'frame');
    if (!frame) return;
    fillFrameWithImage(frame, asset);
  }, [fillFrameWithImage, renderedElements]);

  const handleSaveSelectedAsUpload = useCallback(async () => {
    if (!firebase || !studioRootFolderId || selectedIds.length !== 1) return;
    const elementId = selectedIds[0];
    const node = viewportRef.current?.querySelector(`[data-element-id="${elementId}"]`);
    if (!node) {
      setExportError('Unable to find the selected element on canvas.');
      return;
    }
    setExportError('');
    setExportStatus('Exporting element…');
    try {
      const captureScale = Math.min(4, Math.max(1, 2 / stageControls.zoom));
      const canvas = await html2canvas(node, {
        backgroundColor: null,
        useCORS: true,
        scale: captureScale,
        logging: false,
      });
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((result) => {
          if (!result) {
            reject(new Error('Unable to export element.'));
            return;
          }
          resolve(result);
        }, 'image/png');
      });
      const file = new File([blob], `element-${elementId}.png`, { type: 'image/png' });
      await driveUpload(firebase, studioRootFolderId, file, { rootType: 'studio' });
      if (refreshDriveAssets) {
        await refreshDriveAssets();
      }
      setExportStatus('Saved to Drive uploads.');
      setTimeout(() => setExportStatus(''), 2500);
    } catch (error) {
      console.error('Failed to export element:', error);
      setExportStatus('');
      setExportError(error?.message || 'Export failed.');
    }
  }, [firebase, refreshDriveAssets, selectedIds, stageControls.zoom, studioRootFolderId]);

  const handleCloseImageTool = useCallback(() => {
    setImageToolPanel({ open: false, tool: null, targetId: null });
    setCropDraft(null);
    setActiveEraserStroke(null);
    setEraserCursor(null);
  }, []);

  const handleApplyImageCrop = useCallback(() => {
    if (!cropRect || !cropTarget) return;
    const rect = clampCropRect(cropRect, 0.05);
    const isFull =
      rect.x === 0 && rect.y === 0 && rect.w === 1 && rect.h === 1;
    const nextElements = renderedElements.map((el) =>
      el.id === cropTarget.id
        ? {
            ...el,
            crop: {
              ...rect,
              enabled: !isFull,
            },
          }
        : el,
    );
    updateElements(nextElements, { forcePersist: true, commit: true });
    handleCloseImageTool();
  }, [cropRect, cropTarget, renderedElements, updateElements, handleCloseImageTool]);

  const handleCancelImageCrop = useCallback(() => {
    setCropDraft(null);
    handleCloseImageTool();
  }, [handleCloseImageTool]);

  const handleResetImageCrop = useCallback(() => {
    setCropDraft({ x: 0, y: 0, w: 1, h: 1 });
  }, []);

  const handleClearImageEraser = useCallback(() => {
    setActiveEraserStroke(null);
    setEraserCursor(null);
  }, []);

  const startFrameCrop = useCallback((frameId) => {
    const frame = renderedElements.find((el) => el.id === frameId && el.type === 'frame');
    if (!frame || frame.locked || !frame.content || frame.content.type !== 'image') return;
    const draftContent = clampFrameContent(frame, frame.content);
    setFrameCropState({
      active: true,
      frameId: frame.id,
      draftContent,
    });
    setSelectedIds([frame.id]);
    setImageToolPanel({ open: false, tool: null, targetId: null });
    isInteractingRef.current = true;
  }, [renderedElements]);

  const stopFrameCrop = useCallback((commit = false) => {
    if (!frameCropState.active || !frameCropTarget) {
      setFrameCropState({ active: false, frameId: null, draftContent: null });
      if (!interactionRef.current) {
        isInteractingRef.current = false;
      }
      return;
    }
    if (commit && frameCropState.draftContent) {
      const nextElements = renderedElements.map((el) =>
        el.id === frameCropTarget.id
          ? { ...el, content: frameCropState.draftContent }
          : el,
      );
      updateElements(nextElements, { forcePersist: true, commit: true });
    }
    setFrameCropState({ active: false, frameId: null, draftContent: null });
    if (!interactionRef.current) {
      isInteractingRef.current = false;
    }
  }, [frameCropState.active, frameCropState.draftContent, frameCropTarget, renderedElements, updateElements]);

  const resetFrameCrop = useCallback(() => {
    if (!frameCropTarget?.content) return;
    const naturalW = frameCropTarget.content.naturalW || frameCropTarget.w;
    const naturalH = frameCropTarget.content.naturalH || frameCropTarget.h;
    const coverScale = computeCoverScale(frameCropTarget.w, frameCropTarget.h, naturalW, naturalH);
    const imgW = naturalW * coverScale;
    const imgH = naturalH * coverScale;
    const centered = clampFrameContent(frameCropTarget, {
      ...frameCropTarget.content,
      scale: coverScale,
      offsetX: (frameCropTarget.w - imgW) / 2,
      offsetY: (frameCropTarget.h - imgH) / 2,
    });
    setFrameCropState((prev) => ({ ...prev, draftContent: centered }));
  }, [frameCropTarget]);

  const updateFrameContentScale = useCallback((scale, anchor = null) => {
    const frame = frameCropTarget || selectedElement;
    if (!frame || frame.type !== 'frame' || !frame.content) return;

    const draft =
      frameCropState.active && frameCropTarget?.id === frame.id
        ? frameCropState.draftContent
        : frame.content;
    if (!draft) return;

    const naturalW = draft.naturalW || frame.w;
    const naturalH = draft.naturalH || frame.h;
    const coverScale = computeCoverScale(frame.w, frame.h, naturalW, naturalH);
    const currentScale = draft.scale || coverScale;
    const nextScale = Math.max(coverScale, Math.min(scale, coverScale * 5));

    const baseOffsetX = draft.offsetX || 0;
    const baseOffsetY = draft.offsetY || 0;
    let nextOffsetX = baseOffsetX;
    let nextOffsetY = baseOffsetY;

    if (anchor) {
      const imgW = naturalW * currentScale;
      const imgH = naturalH * currentScale;
      const ax = imgW ? (anchor.x - baseOffsetX) / imgW : 0.5;
      const ay = imgH ? (anchor.y - baseOffsetY) / imgH : 0.5;
      nextOffsetX = anchor.x - ax * (naturalW * nextScale);
      nextOffsetY = anchor.y - ay * (naturalH * nextScale);
    } else {
      nextOffsetX = baseOffsetX + (naturalW * currentScale - naturalW * nextScale) / 2;
      nextOffsetY = baseOffsetY + (naturalH * currentScale - naturalH * nextScale) / 2;
    }

    const nextContent = clampFrameContent(frame, {
      ...draft,
      scale: nextScale,
      offsetX: nextOffsetX,
      offsetY: nextOffsetY,
    });

    if (frameCropState.active && frameCropTarget?.id === frame.id) {
      setFrameCropState((prev) => ({ ...prev, draftContent: nextContent }));
    } else {
      const nextElements = renderedElements.map((el) =>
        el.id === frame.id ? { ...el, content: nextContent } : el,
      );
      updateElements(nextElements);
    }
  }, [
    frameCropState.active,
    frameCropState.draftContent,
    frameCropTarget,
    renderedElements,
    selectedElement,
    updateElements,
  ]);

  const updateBackgroundImageScale = useCallback(
    (scale, anchor = null) => {
      if (!backgroundImageEnabled || !backgroundImage) return;
      const frame = { w: artboardWidth || 1, h: artboardHeight || 1 };
      const naturalW = backgroundImage.naturalW || frame.w;
      const naturalH = backgroundImage.naturalH || frame.h;
      const coverScale = computeCoverScale(frame.w, frame.h, naturalW, naturalH);
      const currentScale = backgroundImage.scale || coverScale;
      const nextScale = Math.max(coverScale, Math.min(scale, coverScale * 5));

      const baseOffsetX = backgroundImage.offsetX || 0;
      const baseOffsetY = backgroundImage.offsetY || 0;
      let nextOffsetX = baseOffsetX;
      let nextOffsetY = baseOffsetY;

      if (anchor) {
        const imgW = naturalW * currentScale;
        const imgH = naturalH * currentScale;
        const ax = imgW ? (anchor.x - baseOffsetX) / imgW : 0.5;
        const ay = imgH ? (anchor.y - baseOffsetY) / imgH : 0.5;
        nextOffsetX = anchor.x - ax * (naturalW * nextScale);
        nextOffsetY = anchor.y - ay * (naturalH * nextScale);
      } else {
        nextOffsetX = baseOffsetX + (naturalW * currentScale - naturalW * nextScale) / 2;
        nextOffsetY = baseOffsetY + (naturalH * currentScale - naturalH * nextScale) / 2;
      }

      const clamped = clampFrameContent(frame, {
        type: 'image',
        naturalW,
        naturalH,
        scale: nextScale,
        offsetX: nextOffsetX,
        offsetY: nextOffsetY,
      });

      applyBackgroundImageDraft(
        {
          ...backgroundImage,
          scale: clamped?.scale ?? nextScale,
          offsetX: clamped?.offsetX ?? nextOffsetX,
          offsetY: clamped?.offsetY ?? nextOffsetY,
          userScaled: true,
        },
        { commit: false },
      );
    },
    [
      applyBackgroundImageDraft,
      artboardHeight,
      artboardWidth,
      backgroundImage,
      backgroundImageEnabled,
    ],
  );

  const handleFrameCropPointerDown = useCallback((event, frame, options = {}) => {
    if (!frame || frame.type !== 'frame') return;
    if (options.autoStart && (!frameCropTarget || frame.id !== frameCropTarget.id)) {
      if (!frame.content || frame.content.type !== 'image' || frame.locked) return;
      const draftContent = clampFrameContent(frame, frame.content);
      setFrameCropState({
        active: true,
        frameId: frame.id,
        draftContent,
      });
      setSelectedIds([frame.id]);
      setImageToolPanel({ open: false, tool: null, targetId: null });
      isInteractingRef.current = true;
      // Use draftContent immediately for this drag
      event.preventDefault();
      event.stopPropagation();
      capturePointer(event);
      const startLocal = getFrameLocalPointFromEvent(frame, event);
      if (!isLocalPointInside(frame, startLocal)) return;
      beginInteraction({
        mode: 'frame-crop-drag',
        pointerId: event.pointerId,
        frameId: frame.id,
        startLocal,
        startContent: draftContent,
      });
      return;
    }
    if (!frameCropTarget || frame.id !== frameCropTarget.id) return;
    if (!frameCropState.draftContent) return;
    event.preventDefault();
    event.stopPropagation();
    capturePointer(event);
    const startLocal = getFrameLocalPointFromEvent(frame, event);
    if (!isLocalPointInside(frame, startLocal)) return;
    beginInteraction({
      mode: 'frame-crop-drag',
      pointerId: event.pointerId,
      frameId: frame.id,
      startLocal,
      startContent: frameCropState.draftContent,
    });
  }, [
    frameCropState.draftContent,
    frameCropTarget,
    getFrameLocalPointFromEvent,
    isLocalPointInside,
    setImageToolPanel,
  ]);

  const handleFrameCropWheel = useCallback((event) => {
    if (!frameCropTarget || !frameCropState.draftContent) return false;
    const local = worldToElementLocal(frameCropTarget, getWorldPointFromEvent(event));
    if (!isLocalPointInside(frameCropTarget, local)) return false;
    const naturalW = frameCropState.draftContent.naturalW || frameCropTarget.w;
    const naturalH = frameCropState.draftContent.naturalH || frameCropTarget.h;
    const coverScale = computeCoverScale(frameCropTarget.w, frameCropTarget.h, naturalW, naturalH);
    const currentScale = frameCropState.draftContent.scale || coverScale;
    const factor = event.deltaY > 0 ? 0.94 : 1.06;
    const nextScale = Math.max(coverScale, Math.min(currentScale * factor, coverScale * 5));
    updateFrameContentScale(nextScale, { x: local.x, y: local.y });
    return true;
  }, [
    frameCropState.draftContent,
    frameCropTarget,
    getWorldPointFromEvent,
    isLocalPointInside,
    updateFrameContentScale,
    worldToElementLocal,
  ]);

  const handleCanvasWheelOverride = useCallback((event) => {
    if (handleFrameCropWheel(event)) return true;
    if (!isBackgroundRepositioning || !backgroundImageEnabled) return false;
    if (!backgroundImage) return false;
    const world = getWorldPointFromEvent(event);
    if (
      world.x < 0
      || world.y < 0
      || world.x > artboardWidth
      || world.y > artboardHeight
    ) {
      return false;
    }
    const factor = event.deltaY > 0 ? 0.94 : 1.06;
    const coverScale = getBackgroundCoverScale(backgroundImage);
    const currentScale = backgroundImage.scale || coverScale;
    updateBackgroundImageScale(currentScale * factor, { x: world.x, y: world.y });
    return true;
  }, [
    artboardHeight,
    artboardWidth,
    backgroundImage,
    backgroundImageEnabled,
    getBackgroundCoverScale,
    getWorldPointFromEvent,
    handleFrameCropWheel,
    isBackgroundRepositioning,
    updateBackgroundImageScale,
  ]);

  const handleCropPointerDown = useCallback(
    (event, handle) => {
      if (!cropTarget || !cropRect) return;
      const bounds = getElementViewportBounds(cropTarget);
      if (!bounds) return;
      event.preventDefault();
      event.stopPropagation();
      capturePointer(event);
      const startPoint = getNormalizedPoint(event, bounds);
      beginInteraction({
        mode: 'crop',
        pointerId: event.pointerId,
        targetId: cropTarget.id,
        bounds,
        handle,
        startPoint,
        startRect: cropRect,
      });
    },
    [cropRect, cropTarget],
  );

  const handleImageEraserPointerDown = useCallback(
    (event, element) => {
      if (!imageToolPanel?.open || imageToolPanel.tool !== 'eraser') return;
      if (!element || element.locked) return;
      const eraserMode = element.eraser?.mode || 'pixel';
      if (eraserMode === 'magic') {
        const worldPoint = getWorldPointFromEvent(event);
        const normalized = mapWorldPointToImageNormalized({
          worldPoint,
          element,
        });
        if (!normalized) return;
        const settings = element.eraser?.magic || {};
        const op = createMagicOp({ point: normalized, settings });
        const nextElements = renderedElements.map((el) =>
          el.id === element.id
            ? {
                ...el,
                eraser: {
                  ...(el.eraser || {}),
                  enabled: true,
                  mode: 'magic',
                  magic: {
                    ...(el.eraser?.magic || {}),
                    ops: [...(el.eraser?.magic?.ops || []), op],
                  },
                },
              }
            : el,
        );
        updateElements(nextElements, { forcePersist: true, commit: true });
        return;
      }
      const bounds = getElementViewportBounds(element);
      if (!bounds) return;
      event.preventDefault();
      event.stopPropagation();
      capturePointer(event);
      const point = getNormalizedPointFromBounds(event, bounds);
      const brushSize = element.eraser?.brushSize ?? 24;
      const stroke = createStroke({
        point,
        size: brushSize,
        hardness: element.eraser?.hardness ?? 0.8,
        opacity: element.eraser?.opacity ?? 1,
      });
      beginInteraction({
        mode: 'erasing',
        pointerId: event.pointerId,
        targetId: element.id,
        bounds,
        stroke,
      });
      setActiveEraserStroke({ ...stroke, points: [...stroke.points] });
      const viewportPoint = getViewportPoint(event, viewportRef);
      setEraserCursor({
        x: viewportPoint.x,
        y: viewportPoint.y,
        size: brushSize * stageControls.zoom,
        mode: 'pixel',
      });
    },
    [imageToolPanel, stageControls.zoom],
  );

  useEffect(() => {
    if (!imageToolPanel?.open) return;
    if (!imageToolTarget || imageToolTarget.type !== 'image') {
      handleCloseImageTool();
      return;
    }
    if (!selectedIds.includes(imageToolTarget.id)) {
      setSelectedIds([imageToolTarget.id]);
    }
    if (imageToolPanel.tool === 'crop' && !cropDraft) {
      const initialCrop = imageToolTarget.crop?.enabled
        ? imageToolTarget.crop
        : { x: 0, y: 0, w: 1, h: 1 };
      setCropDraft({ ...initialCrop });
    }
  }, [
    cropDraft,
    handleCloseImageTool,
    imageToolPanel,
    imageToolTarget,
    selectedIds,
  ]);

  const startTextEdit = (elementId) => {
    const element = renderedElements.find((item) => item.id === elementId);
    if (!element || element.type !== 'text' || element.locked) return;
    setSelectedIds([elementId]);
    setEditingTextId(elementId);
    setEditingTextOriginal(element.text || '');
    setEditingTextValue(element.text || '');
    setInteractionMode('textEditing');
    isInteractingRef.current = true;
  };

  const handleTextChange = (value) => {
    setEditingTextValue(value);
    if (!editingTextId) return;
    const nextElements = renderedElements.map((item) => {
      if (item.id !== editingTextId) return item;
      const autoWidth = item.autoWidth !== false;
      const autoHeight = item.autoHeight !== false;
      let nextW = item.w;
      let nextH = item.h;
      if (autoWidth || autoHeight) {
        const measure = measureTextBox({
          text: value,
          wrapWidth: autoWidth ? undefined : item.w,
          autoWidth,
          fontSize: item.fontSize || 20,
          fontFamily: item.fontFamily || 'Inter, sans-serif',
          fontWeight: item.fontWeight || 400,
          fontStyle: item.fontStyle || 'normal',
          lineHeight: item.lineHeight || 1.2,
          letterSpacing: item.letterSpacing || 0,
          paddingX: item.paddingX || 0,
          paddingY: item.paddingY || 0,
          minW: item.minW || 40,
        });
        if (autoWidth) {
          nextW = measure.tightW;
        }
        if (autoHeight) {
          nextH = measure.tightH;
        }
      }
      return {
        ...item,
        text: value,
        w: nextW,
        h: nextH,
      };
    });
    if (usingDemoElements) {
      setDemoElements(nextElements);
      return;
    }
    setElementsLocal(nextElements);
    textEditCommit.schedule(sanitizeDeep(nextElements), 'text-edit');
  };

  const commitTextEdit = () => {
    if (!editingTextId) return;
    const element = renderedElements.find((item) => item.id === editingTextId);
    if (!element) {
      setEditingTextId(null);
      setEditingTextValue('');
      setInteractionMode('idle');
      isInteractingRef.current = false;
      return;
    }
    const nextText = editingTextValue ?? '';
    const autoWidth = element.autoWidth !== false;
    const autoHeight = element.autoHeight !== false;
    const measure = measureTextBox({
      text: nextText,
      wrapWidth: autoWidth ? undefined : element.w,
      autoWidth,
      fontSize: element.fontSize || 20,
      fontFamily: element.fontFamily || 'Inter, sans-serif',
      fontWeight: element.fontWeight || 400,
      fontStyle: element.fontStyle || 'normal',
      lineHeight: element.lineHeight || 1.2,
      letterSpacing: element.letterSpacing || 0,
      paddingX: element.paddingX || 0,
      paddingY: element.paddingY || 0,
      minW: element.minW || 40,
    });
    const nextWidth = autoWidth ? measure.tightW : element.w;
    const nextHeight = autoHeight ? measure.tightH : element.h;
    const nextElements = renderedElements.map((item) => (
      item.id === editingTextId
        ? { ...item, text: nextText, w: nextWidth, h: nextHeight }
        : item
    ));
    if (usingDemoElements) {
      setDemoElements(nextElements);
    } else {
      setElementsLocal(nextElements);
      textEditCommit.cancel();
      commitElementsToFirestore(sanitizeDeep(nextElements), 'text-edit-commit');
    }
    setEditingTextId(null);
    setEditingTextValue('');
    setInteractionMode('idle');
    isInteractingRef.current = false;
  };

  const cancelTextEdit = () => {
    if (editingTextId) {
      const nextElements = renderedElements.map((item) => (
        item.id === editingTextId ? { ...item, text: editingTextOriginal } : item
      ));
      if (usingDemoElements) {
        setDemoElements(nextElements);
      } else {
        setElementsLocal(nextElements);
      }
    }
    setEditingTextId(null);
    setEditingTextValue('');
    setInteractionMode('idle');
    isInteractingRef.current = false;
  };

  const beginInteraction = (payload) => {
    interactionRef.current = payload;
    setInteractionMode(payload.mode);
    isInteractingRef.current = true;
  };

  const clearInteraction = () => {
    interactionRef.current = null;
    setInteractionMode('idle');
    setMarquee(null);
    setSnapGuides({ x: [], y: [] });
    setResizeHud(null);
    isInteractingRef.current = false;
  };

  const getMovableSelection = (ids) => {
    const selected = renderedElements.filter((el) => ids.includes(el.id));
    const movable = selected.filter((el) => !el.locked);
    return { selected, movable };
  };

  const applyElementsLocal = (nextElements) => {
    updateElements(nextElements, { commit: false });
  };

  const commitElements = () => {
    const latestElements = elementsRef.current;
    if (usingDemoElements) {
      updateElements(latestElements, { commit: false });
      return;
    }
    updateElements(latestElements, { forcePersist: true, commit: true });
  };

  const handleElementPointerDown = (event, elementId) => {
    event.stopPropagation();
    if (isSpacePressed || editingTextId || isImageToolBlocking || isBackgroundRepositioning) return;
    if (event.button && event.button !== 0) return;
    const now = Date.now();
    const lastClick = lastClickRef.current;
    const targetElement = renderedElements.find((el) => el.id === elementId);
    if (
      targetElement?.type === 'frame'
      && targetElement.content?.type === 'image'
      && !targetElement.locked
      && lastClick.id === elementId
      && now - lastClick.time < 320
    ) {
      lastClickRef.current = { id: null, time: 0 };
      startFrameCrop(elementId);
      return;
    }
    if (
      targetElement?.type === 'text'
      && !targetElement.locked
      && lastClick.id === elementId
      && now - lastClick.time < 320
    ) {
      lastClickRef.current = { id: null, time: 0 };
      startTextEdit(elementId);
      return;
    }
    lastClickRef.current = { id: elementId, time: now };
    capturePointer(event);
    const isSelected = selectedIds.includes(elementId);
    let nextSelection = selectedIds;
    if (event.shiftKey) {
      nextSelection = isSelected
        ? selectedIds.filter((id) => id !== elementId)
        : [...selectedIds, elementId];
      setSelectedIds(nextSelection);
      if (!nextSelection.includes(elementId)) {
        return;
      }
    } else if (!isSelected) {
      nextSelection = [elementId];
      setSelectedIds(nextSelection);
    }

    const { movable } = getMovableSelection(nextSelection);
    if (!movable.length) {
      return;
    }

    const startWorld = getWorldPointFromEvent(event);
    const startBounds = getBoundsFromElements(movable);
    const guides = buildSnapGuides(renderedElements, nextSelection, artboardWidth, artboardHeight);
    beginInteraction({
      mode: 'dragging',
      pointerId: event.pointerId,
      startWorld,
      startBounds,
      startElements: movable.map((el) => ({ ...el })),
      selectedIds: nextSelection,
      guides,
    });
  };

  const handleWorkspacePointerDown = (event) => {
    if (eyedropperActiveRef.current) {
      event.preventDefault();
      event.stopPropagation();
      pickEyedropperColor(event);
      return;
    }
    if (frameCropTarget && frameCropState?.active && frameCropState?.draftContent) {
      const startLocal = getFrameLocalPointFromEvent(frameCropTarget, event);
      if (isLocalPointInside(frameCropTarget, startLocal)) {
        event.preventDefault();
        capturePointer(event);
        beginInteraction({
          mode: 'frame-crop-drag',
          pointerId: event.pointerId,
          frameId: frameCropTarget.id,
          startLocal,
          startContent: frameCropState.draftContent,
        });
        return;
      }
    }
    if (isBackgroundRepositioning && backgroundImageEnabled) {
      if (event.button && event.button !== 0) return;
      const worldPoint = getWorldPointFromEvent(event);
      if (
        worldPoint.x >= 0
        && worldPoint.y >= 0
        && worldPoint.x <= artboardWidth
        && worldPoint.y <= artboardHeight
      ) {
        event.preventDefault();
        capturePointer(event);
        beginInteraction({
          mode: 'page-bg-drag',
          pointerId: event.pointerId,
          startWorld: worldPoint,
          startBackground: backgroundImage,
        });
      }
      return;
    }
    if (isSpacePressed || isImageToolBlocking) return;
    if (event.button && event.button !== 0) return;
    if (editingTextId) {
      commitTextEdit();
    }
    event.preventDefault();
    capturePointer(event);
    const startViewport = getViewportPoint(event, viewportRef);
    const startWorld = viewportToWorld(startViewport, stageControls.pan, stageControls.zoom);
    beginInteraction({
      mode: 'marquee',
      pointerId: event.pointerId,
      startViewport,
      startWorld,
      shiftKey: event.shiftKey,
      initialSelection: selectedIds,
    });
    if (!event.shiftKey) {
      setSelectedIds([]);
    }
    setMarquee({
      x: startViewport.x,
      y: startViewport.y,
      w: 0,
      h: 0,
    });
  };

  const handleTransformHandlePointerDown = (event, handle) => {
    if (!selectionBoundsWorld || editingTextId || isImageToolBlocking) return;
    event.preventDefault();
    capturePointer(event);
    const { movable } = getMovableSelection(selectedIds);
    if (!movable.length) return;
    const textOnly = movable.every((el) => el.type === 'text');
    const cornerHandles = ['nw', 'ne', 'sw', 'se'];
    if (textOnly && !cornerHandles.includes(handle)) {
      return;
    }
    const startWorld = getWorldPointFromEvent(event);
    const guides = buildSnapGuides(renderedElements, selectedIds, artboardWidth, artboardHeight);
    beginInteraction({
      mode: 'resizing',
      pointerId: event.pointerId,
      handle,
      startWorld,
      startBounds: selectionBoundsWorld,
      startElements: movable.map((el) => ({ ...el })),
      guides,
      isTextResize: textOnly,
    });
  };

  const handleRotatePointerDown = (event) => {
    if (!selectionBoundsWorld || editingTextId || isImageToolBlocking) return;
    event.preventDefault();
    capturePointer(event);
    const { movable } = getMovableSelection(selectedIds);
    if (!movable.length) return;
    const startWorld = getWorldPointFromEvent(event);
    const center = {
      x: selectionBoundsWorld.x + selectionBoundsWorld.w / 2,
      y: selectionBoundsWorld.y + selectionBoundsWorld.h / 2,
    };
    const startAngle = Math.atan2(startWorld.y - center.y, startWorld.x - center.x);
    beginInteraction({
      mode: 'rotating',
      pointerId: event.pointerId,
      center,
      startAngle,
      startElements: movable.map((el) => ({ ...el })),
    });
  };

  const applyDrag = (interaction, currentWorld, event) => {
    const dxRaw = currentWorld.x - interaction.startWorld.x;
    const dyRaw = currentWorld.y - interaction.startWorld.y;
    let dx = dxRaw;
    let dy = dyRaw;
    if (event.shiftKey) {
      if (Math.abs(dx) > Math.abs(dy)) {
        dy = 0;
      } else {
        dx = 0;
      }
    }

    const proposedBounds = {
      x: interaction.startBounds.x + dx,
      y: interaction.startBounds.y + dy,
      w: interaction.startBounds.w,
      h: interaction.startBounds.h,
    };
    const snap = getSnapDelta(proposedBounds, interaction.guides, 6 / stageControls.zoom);
    dx += snap.dx;
    dy += snap.dy;
    setSnapGuides({
      x: snap.guideX != null
        ? [worldToViewport({ x: snap.guideX, y: 0 }, stageControls.pan, stageControls.zoom).x]
        : [],
      y: snap.guideY != null
        ? [worldToViewport({ x: 0, y: snap.guideY }, stageControls.pan, stageControls.zoom).y]
        : [],
    });

    const nextElements = renderedElements.map((el) => {
      const startEl = interaction.startElements.find((item) => item.id === el.id);
      if (!startEl) return el;
      return {
        ...el,
        x: startEl.x + dx,
        y: startEl.y + dy,
      };
    });
    applyElementsLocal(nextElements);
  };

  const applyResize = (interaction, currentWorld, event) => {
    const { startBounds, handle } = interaction;
    const dx = currentWorld.x - interaction.startWorld.x;
    const dy = currentWorld.y - interaction.startWorld.y;

    if (interaction.isTextResize) {
      let left = startBounds.x;
      let right = startBounds.x + startBounds.w;

      if (handle.includes('e')) right += dx;
      if (handle.includes('w')) left += dx;

      let newW = right - left;
      const minGroupW = Math.max(
        40,
        ...interaction.startElements.map((el) => el.minW || 40),
      );
      newW = Math.max(newW, minGroupW);
      right = left + newW;

      const proposedBounds = {
        x: left,
        y: startBounds.y,
        w: newW,
        h: startBounds.h,
      };
      const snap = getSnapDelta(proposedBounds, interaction.guides, 6 / stageControls.zoom);
      if (snap.guideX != null) {
        left += snap.dx;
        right += snap.dx;
      }

      const finalBounds = {
        x: left,
        y: startBounds.y,
        w: right - left,
        h: startBounds.h,
      };
      const scaleX = finalBounds.w / startBounds.w;

      const nextElements = renderedElements.map((el) => {
        const startEl = interaction.startElements.find((item) => item.id === el.id);
        if (!startEl) return el;
        const relativeX = startEl.x - startBounds.x;
        const nextW = Math.max(startEl.w * scaleX, startEl.minW || 40);
        const autoHeight = startEl.autoHeight !== false;
        const measure = measureTextBox({
          text: startEl.text,
          wrapWidth: nextW,
          autoWidth: false,
          fontSize: startEl.fontSize || 20,
          fontFamily: startEl.fontFamily || 'Inter, sans-serif',
          fontWeight: startEl.fontWeight || 400,
          fontStyle: startEl.fontStyle || 'normal',
          lineHeight: startEl.lineHeight || 1.2,
          letterSpacing: startEl.letterSpacing || 0,
          paddingX: startEl.paddingX || 0,
          paddingY: startEl.paddingY || 0,
          minW: startEl.minW || 40,
        });
        const nextH = autoHeight ? measure.tightH : startEl.h;
        return {
          ...el,
          x: finalBounds.x + relativeX * scaleX,
          y: startEl.y,
          w: nextW,
          h: nextH,
          autoWidth: false,
        };
      });

      const resizedSelection = nextElements.filter((el) =>
        interaction.startElements.some((item) => item.id === el.id),
      );
      const resizedBounds = getBoundsFromElements(resizedSelection);
      const hudPoint = getViewportPoint(event, viewportRef);
      setResizeHud({
        x: hudPoint.x,
        y: hudPoint.y,
        label: `${Math.round(resizedBounds.w)} x ${Math.round(resizedBounds.h)}`,
      });
      setSnapGuides({
        x: snap.guideX != null
          ? [worldToViewport({ x: snap.guideX, y: 0 }, stageControls.pan, stageControls.zoom).x]
          : [],
        y: [],
      });
      applyElementsLocal(nextElements);
      return;
    }
    const fromCenter = event.altKey;
    const keepRatio = event.shiftKey;
    const ratio = startBounds.w / startBounds.h;
    let left = startBounds.x;
    let right = startBounds.x + startBounds.w;
    let top = startBounds.y;
    let bottom = startBounds.y + startBounds.h;

    const applyAxis = () => {
      if (handle.includes('e')) right += dx;
      if (handle.includes('w')) left += dx;
      if (handle.includes('s')) bottom += dy;
      if (handle.includes('n')) top += dy;

      if (fromCenter) {
        if (handle.includes('e') || handle.includes('w')) {
          const deltaX = dx;
          left = startBounds.x - deltaX;
          right = startBounds.x + startBounds.w + deltaX;
        }
        if (handle.includes('n') || handle.includes('s')) {
          const deltaY = dy;
          top = startBounds.y - deltaY;
          bottom = startBounds.y + startBounds.h + deltaY;
        }
      }
    };

    applyAxis();

    let newW = right - left;
    let newH = bottom - top;

    if (keepRatio) {
      if (handle === 'n' || handle === 's') {
        newW = newH * ratio;
      } else if (handle === 'e' || handle === 'w') {
        newH = newW / ratio;
      } else {
        const dominant = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (dominant === 'x') {
          newH = newW / ratio;
        } else {
          newW = newH * ratio;
        }
      }
      if (handle.includes('w')) {
        left = right - newW;
      } else {
        right = left + newW;
      }
      if (handle.includes('n')) {
        top = bottom - newH;
      } else {
        bottom = top + newH;
      }
    }

    const minW = 24;
    const minH = 24;
    newW = Math.max(newW, minW);
    newH = Math.max(newH, minH);
    right = left + newW;
    bottom = top + newH;

    const proposedBounds = { x: left, y: top, w: newW, h: newH };
    const snap = getSnapDelta(proposedBounds, interaction.guides, 6 / stageControls.zoom);
    if (snap.guideX != null) {
      if (handle.includes('w')) {
        left += snap.dx;
      } else if (handle.includes('e')) {
        right += snap.dx;
      } else {
        left += snap.dx;
        right += snap.dx;
      }
    }
    if (snap.guideY != null) {
      if (handle.includes('n')) {
        top += snap.dy;
      } else if (handle.includes('s')) {
        bottom += snap.dy;
      } else {
        top += snap.dy;
        bottom += snap.dy;
      }
    }

    const finalBounds = {
      x: left,
      y: top,
      w: right - left,
      h: bottom - top,
    };

    const hudPoint = getViewportPoint(event, viewportRef);
    setResizeHud({
      x: hudPoint.x,
      y: hudPoint.y,
      label: `${Math.round(finalBounds.w)} x ${Math.round(finalBounds.h)}`,
    });

    setSnapGuides({
      x: snap.guideX != null
        ? [worldToViewport({ x: snap.guideX, y: 0 }, stageControls.pan, stageControls.zoom).x]
        : [],
      y: snap.guideY != null
        ? [worldToViewport({ x: 0, y: snap.guideY }, stageControls.pan, stageControls.zoom).y]
        : [],
    });

    const scaleX = finalBounds.w / startBounds.w;
    const scaleY = finalBounds.h / startBounds.h;
    const nextElements = renderedElements.map((el) => {
      const startEl = interaction.startElements.find((item) => item.id === el.id);
      if (!startEl) return el;
      const relativeX = startEl.x - startBounds.x;
      const relativeY = startEl.y - startBounds.y;
      const minWidth = startEl.type === 'text' ? 48 : minW;
      const minHeight = startEl.type === 'text'
        ? Math.round((startEl.fontSize || 20) * 1.2)
        : minH;
      const nextX = finalBounds.x + relativeX * scaleX;
      const nextY = finalBounds.y + relativeY * scaleY;
      const nextW = Math.max(startEl.w * scaleX, minWidth);
      const nextH = Math.max(startEl.h * scaleY, minHeight);

      if (startEl.type === 'frame' && startEl.content?.type === 'image') {
        const naturalW = startEl.content.naturalW || startEl.w;
        const naturalH = startEl.content.naturalH || startEl.h;
        const prevScale =
          startEl.content.scale || computeCoverScale(startEl.w, startEl.h, naturalW, naturalH);
        const newCoverScale = computeCoverScale(nextW, nextH, naturalW, naturalH);
        const nextScale = Math.max(prevScale, newCoverScale);

        const imgWPrev = naturalW * prevScale;
        const imgHPrev = naturalH * prevScale;
        const focalX = imgWPrev
          ? (-(startEl.content.offsetX || 0) + startEl.w / 2) / imgWPrev
          : 0.5;
        const focalY = imgHPrev
          ? (-(startEl.content.offsetY || 0) + startEl.h / 2) / imgHPrev
          : 0.5;

        const nextContentRaw = {
          ...startEl.content,
          scale: nextScale,
          offsetX: nextW / 2 - focalX * (naturalW * nextScale),
          offsetY: nextH / 2 - focalY * (naturalH * nextScale),
        };

        const nextFrame = { ...startEl, w: nextW, h: nextH };
        return {
          ...el,
          x: nextX,
          y: nextY,
          w: nextW,
          h: nextH,
          content: clampFrameContent(nextFrame, nextContentRaw),
        };
      }

      return {
        ...el,
        x: nextX,
        y: nextY,
        w: nextW,
        h: nextH,
      };
    });
    applyElementsLocal(nextElements);
  };

  const applyRotate = (interaction, currentWorld, event) => {
    const { center } = interaction;
    const startAngle = interaction.startAngle;
    const currentAngle = Math.atan2(currentWorld.y - center.y, currentWorld.x - center.x);
    let deltaDeg = (currentAngle - startAngle) * (180 / Math.PI);
    if (event.shiftKey) {
      const snap = 15;
      deltaDeg = Math.round(deltaDeg / snap) * snap;
    }
    const deltaRad = (deltaDeg * Math.PI) / 180;
    const cos = Math.cos(deltaRad);
    const sin = Math.sin(deltaRad);

    const nextElements = renderedElements.map((el) => {
      const startEl = interaction.startElements.find((item) => item.id === el.id);
      if (!startEl) return el;
      const startCenter = {
        x: startEl.x + startEl.w / 2,
        y: startEl.y + startEl.h / 2,
      };
      const relX = startCenter.x - center.x;
      const relY = startCenter.y - center.y;
      const rotatedCenter = {
        x: center.x + relX * cos - relY * sin,
        y: center.y + relX * sin + relY * cos,
      };
      return {
        ...el,
        x: rotatedCenter.x - startEl.w / 2,
        y: rotatedCenter.y - startEl.h / 2,
        rotation: (startEl.rotation || 0) + deltaDeg,
      };
    });
    applyElementsLocal(nextElements);
  };

  const handleWorkspacePointerMove = (event) => {
    const interaction = interactionRef.current;
    if (!interaction) {
      if (imageToolPanel?.open && imageToolPanel.tool === 'eraser' && imageToolTarget) {
        const viewportPoint = getViewportPoint(event, viewportRef);
        const mode = imageToolTarget.eraser?.mode || 'pixel';
        if (mode === 'magic') {
          const tolerance = imageToolTarget.eraser?.magic?.tolerance ?? 25;
          setEraserCursor({
            x: viewportPoint.x,
            y: viewportPoint.y,
            size: 12,
            mode: 'magic',
            label: `Tol ${Math.round(tolerance)}`,
          });
        } else {
          const brushSize = imageToolTarget.eraser?.brushSize ?? 24;
          setEraserCursor({
            x: viewportPoint.x,
            y: viewportPoint.y,
            size: brushSize * stageControls.zoom,
            mode: 'pixel',
          });
        }
      }
      return;
    }
    if (interaction.pointerId && interaction.pointerId !== event.pointerId) return;
    if (interaction.mode === 'frame-crop-drag') {
      const frame = renderedElements.find(
        (el) => el.id === interaction.frameId && el.type === 'frame',
      );
      if (!frame || !frameCropState.draftContent) return;
      const currentLocal = getFrameLocalPointFromEvent(frame, event);
      const dx = currentLocal.x - interaction.startLocal.x;
      const dy = currentLocal.y - interaction.startLocal.y;
      const nextContent = clampFrameContent(frame, {
        ...interaction.startContent,
        offsetX: (interaction.startContent.offsetX || 0) + dx,
        offsetY: (interaction.startContent.offsetY || 0) + dy,
      });
      setFrameCropState((prev) => ({ ...prev, draftContent: nextContent }));
      return;
    }
    if (interaction.mode === 'page-bg-drag') {
      if (!interaction.startBackground) return;
      const currentWorld = getWorldPointFromEvent(event);
      const dx = currentWorld.x - interaction.startWorld.x;
      const dy = currentWorld.y - interaction.startWorld.y;
      const nextBg = {
        ...interaction.startBackground,
        offsetX: (interaction.startBackground.offsetX || 0) + dx,
        offsetY: (interaction.startBackground.offsetY || 0) + dy,
        userMoved: true,
      };
      applyBackgroundImageDraft(nextBg, { commit: false });
      return;
    }
    if (interaction.mode === 'marquee') {
      const current = getViewportPoint(event, viewportRef);
      const x = Math.min(interaction.startViewport.x, current.x);
      const y = Math.min(interaction.startViewport.y, current.y);
      const w = Math.abs(current.x - interaction.startViewport.x);
      const h = Math.abs(current.y - interaction.startViewport.y);
      setMarquee({ x, y, w, h });
      return;
    }
    if (interaction.mode === 'crop') {
      const currentPoint = getNormalizedPoint(event, interaction.bounds);
      const nextRect = updateCropRect({
        rect: interaction.startRect,
        handle: interaction.handle,
        startPoint: interaction.startPoint,
        currentPoint,
      });
      setCropDraft(nextRect);
      return;
    }
    if (interaction.mode === 'erasing') {
      const point = getNormalizedPointFromBounds(event, interaction.bounds);
      addPointToStroke(interaction.stroke, point);
      setActiveEraserStroke({
        ...interaction.stroke,
        points: [...interaction.stroke.points],
      });
      const viewportPoint = getViewportPoint(event, viewportRef);
      setEraserCursor({
        x: viewportPoint.x,
        y: viewportPoint.y,
        size: (interaction.stroke.size || 24) * stageControls.zoom,
        mode: 'pixel',
      });
      return;
    }
    const currentWorld = getWorldPointFromEvent(event);
    if (interaction.mode === 'dragging') {
      applyDrag(interaction, currentWorld, event);
    }
    if (interaction.mode === 'resizing') {
      applyResize(interaction, currentWorld, event);
    }
    if (interaction.mode === 'rotating') {
      applyRotate(interaction, currentWorld, event);
    }
  };

  const handleWorkspacePointerUp = (event) => {
    const interaction = interactionRef.current;
    if (!interaction) return;
    if (interaction.pointerId && interaction.pointerId !== event.pointerId) return;
    if (interaction.mode === 'frame-crop-drag') {
      clearInteraction();
      return;
    }
    if (interaction.mode === 'page-bg-drag') {
      if (pageBackgroundDraftRef.current && !isBackgroundRepositioning) {
        applyBackgroundImageDraft(pageBackgroundDraftRef.current, { commit: true });
      }
      clearInteraction();
      return;
    }
    if (interaction.mode === 'marquee') {
      const selectionRect = marquee;
      if (!selectionRect) {
        clearInteraction();
        return;
      }
      if (selectionRect && selectionRect.w < 3 && selectionRect.h < 3) {
        if (!interaction.shiftKey) {
          setSelectedIds([]);
        }
        clearInteraction();
        return;
      }
      const topLeftWorld = viewportToWorld(
        { x: selectionRect.x, y: selectionRect.y },
        stageControls.pan,
        stageControls.zoom,
      );
      const bottomRightWorld = viewportToWorld(
        { x: selectionRect.x + selectionRect.w, y: selectionRect.y + selectionRect.h },
        stageControls.pan,
        stageControls.zoom,
      );
      const minX = Math.min(topLeftWorld.x, bottomRightWorld.x);
      const maxX = Math.max(topLeftWorld.x, bottomRightWorld.x);
      const minY = Math.min(topLeftWorld.y, bottomRightWorld.y);
      const maxY = Math.max(topLeftWorld.y, bottomRightWorld.y);
      const hits = renderedElements.filter((el) => (
        el.x <= maxX
        && el.x + el.w >= minX
        && el.y <= maxY
        && el.y + el.h >= minY
      )).map((el) => el.id);
      const nextSelection = interaction.shiftKey
        ? Array.from(new Set([...interaction.initialSelection, ...hits]))
        : hits;
      setSelectedIds(nextSelection);
      clearInteraction();
      return;
    }

    if (interaction.mode === 'crop') {
      clearInteraction();
      return;
    }

    if (interaction.mode === 'erasing') {
      const targetId = interaction.targetId;
      const stroke = interaction.stroke;
      if (targetId && stroke) {
        const nextElements = renderedElements.map((el) =>
          el.id === targetId
            ? {
                ...el,
                eraser: {
                  ...(el.eraser || {}),
                  enabled: true,
                  strokes: [...(el.eraser?.strokes || []), stroke],
                },
              }
            : el,
        );
        updateElements(nextElements, { forcePersist: true, commit: true });
      }
      setActiveEraserStroke(null);
      setEraserCursor(null);
      clearInteraction();
      return;
    }

    if (['dragging', 'resizing', 'rotating'].includes(interaction.mode)) {
      commitElements();
      clearInteraction();
      return;
    }
    clearInteraction();
  };

  useEffect(() => {
    if (interactionMode !== 'frame-crop-drag') return undefined;
    const onMove = (event) => handleWorkspacePointerMove(event);
    const onUp = (event) => handleWorkspacePointerUp(event);
    window.addEventListener('pointermove', onMove, { capture: true });
    window.addEventListener('pointerup', onUp, { capture: true });
    window.addEventListener('pointercancel', onUp, { capture: true });
    return () => {
      window.removeEventListener('pointermove', onMove, { capture: true });
      window.removeEventListener('pointerup', onUp, { capture: true });
      window.removeEventListener('pointercancel', onUp, { capture: true });
    };
  }, [interactionMode, handleWorkspacePointerMove, handleWorkspacePointerUp]);

  const handleStartEditingName = () => {
    setDraftName(project?.name || 'Untitled Design');
    setIsEditingName(true);
  };

  const handleCommitName = () => {
    const trimmed = draftName.trim();
    if (!trimmed) {
      setDraftName(project?.name || 'Untitled Design');
      setIsEditingName(false);
      return;
    }
    setProject((prev) => (prev ? { ...prev, name: trimmed } : prev));
    queueSave({ name: trimmed });
    setIsEditingName(false);
  };

  const handleCancelName = () => {
    setDraftName(project?.name || 'Untitled Design');
    setIsEditingName(false);
  };

  const buildResizeDefaults = () => {
    setResizeWidth(String(artboardWidth));
    setResizeHeight(String(artboardHeight));
    setResizeUnit(project?.unit || 'px');
    setResizeScaleContent(false);
    setResizeErrors({});
  };

  const openResizeModal = () => {
    buildResizeDefaults();
    setResizeModalOpen(true);
  };

  const closeResizeModal = () => {
    if (resizeSaving) return;
    setResizeModalOpen(false);
  };

  const validateResize = (widthValue, heightValue) => {
    const errors = {};
    const parsedWidth = Math.round(Number(widthValue));
    const parsedHeight = Math.round(Number(heightValue));

    if (!widthValue) {
      errors.width = 'Width is required.';
    } else if (Number.isNaN(parsedWidth)) {
      errors.width = 'Width must be a number.';
    } else if (parsedWidth < 100) {
      errors.width = 'Minimum width is 100px.';
    } else if (parsedWidth > 8000) {
      errors.width = 'Maximum width is 8000px.';
    }

    if (!heightValue) {
      errors.height = 'Height is required.';
    } else if (Number.isNaN(parsedHeight)) {
      errors.height = 'Height must be a number.';
    } else if (parsedHeight < 100) {
      errors.height = 'Minimum height is 100px.';
    } else if (parsedHeight > 8000) {
      errors.height = 'Maximum height is 8000px.';
    }

    return {
      errors,
      parsedWidth: Number.isNaN(parsedWidth) ? null : parsedWidth,
      parsedHeight: Number.isNaN(parsedHeight) ? null : parsedHeight,
    };
  };

  const handleResizeDimensionChange = (field, value) => {
    if (field === 'width') {
      setResizeWidth(value);
    } else {
      setResizeHeight(value);
    }
    const nextWidth = field === 'width' ? value : resizeWidth;
    const nextHeight = field === 'height' ? value : resizeHeight;
    const { errors } = validateResize(nextWidth, nextHeight);
    setResizeErrors(errors);
  };

  const normalizeDimension = (value) => {
    if (value === '' || value === null || value === undefined) {
      return '';
    }
    const parsed = Math.round(Number(value));
    if (Number.isNaN(parsed)) {
      return value;
    }
    return String(parsed);
  };

  const applyCanvasResize = async () => {
    if (!project || !firebase || !franchiseeId || !projectId) return;

    const { errors, parsedWidth, parsedHeight } = validateResize(
      resizeWidth,
      resizeHeight,
    );
    if (Object.keys(errors).length > 0) {
      setResizeErrors(errors);
      return;
    }

    if (!parsedWidth || !parsedHeight) return;
    setResizeSaving(true);
    setSaveStatus('Saving...');
    setSaveError('');
    setSaveDebug('');

    const sourceElements = usingDemoElements ? demoElements : renderedElements;
    let nextElements = sourceElements;

    if (resizeScaleContent) {
      const scaleX = parsedWidth / artboardWidth;
      const scaleY = parsedHeight / artboardHeight;
      nextElements = sourceElements.map((el) => ({
        ...el,
        x: Math.round(el.x * scaleX),
        y: Math.round(el.y * scaleY),
        w: Math.round(el.w * scaleX),
        h: Math.round(el.h * scaleY),
      }));
    } else {
      nextElements = sourceElements.map((el) => {
        let nextX = el.x;
        let nextY = el.y;
        if (el.w > parsedWidth) {
          nextX = 0;
        } else {
          nextX = Math.min(el.x, parsedWidth - el.w);
          nextX = Math.max(0, nextX);
        }
        if (el.h > parsedHeight) {
          nextY = 0;
        } else {
          nextY = Math.min(el.y, parsedHeight - el.h);
          nextY = Math.max(0, nextY);
        }
        return {
          ...el,
          x: nextX,
          y: nextY,
        };
      });
    }

    try {
      if (usingDemoElements) {
        setDemoElements(nextElements);
      } else if (activePage && pageIndex >= 0) {
        const nextPages = editorPages.map((page, index) => (
          index === pageIndex ? { ...page, elements: nextElements } : page
        ));
        setEditorPages(nextPages);
        setProject((prev) => (
          prev
            ? {
                ...prev,
                width: parsedWidth,
                height: parsedHeight,
                unit: resizeUnit || 'px',
                pages: nextPages,
              }
            : prev
        ));
        queueSave(
          {
            width: parsedWidth,
            height: parsedHeight,
            unit: resizeUnit || 'px',
            pages: nextPages,
            activePageId,
          },
          { immediate: true, reason: 'resize-canvas' },
        );
      }

      if (usingDemoElements) {
        setProject((prev) => (
          prev
            ? {
                ...prev,
                width: parsedWidth,
                height: parsedHeight,
                unit: resizeUnit || 'px',
              }
            : prev
        ));
        queueSave(
          {
            width: parsedWidth,
            height: parsedHeight,
            unit: resizeUnit || 'px',
          },
          { immediate: true, reason: 'resize-canvas' },
        );
      }

      setResizeModalOpen(false);
      setSaveStatus('Saved');
      setTimeout(() => stageControls.fitToScreen(), 0);
    } catch (error) {
      console.error('Failed to resize canvas:', error);
      setSaveStatus('Saved');
      setSaveError('Resize failed. Please try again.');
      if (process.env.NODE_ENV !== 'production') {
        setSaveDebug(error?.message || 'Unknown error');
      }
    } finally {
      setResizeSaving(false);
    }
  };

  const { isSpacePressed } = useKeyboardShortcuts({
    onEscape: () => {
      if (editingTextId) {
        setEditingTextId(null);
        setEditingTextValue('');
        return;
      }
      setSelectedIds([]);
    },
    onDelete: handleDelete,
    onDuplicate: handleDuplicate,
    onCopy: () => {
      if (!selectedIds.length) return;
      clipboardRef.current = {
        elements: normalizeClipboard(selectedElements),
        offset: 0,
      };
    },
    onPaste: () => {
      if (!clipboardRef.current.elements.length) return;
      const offset = clipboardRef.current.offset + 10;
      clipboardRef.current.offset = offset;
      const clones = cloneElementsWithOffset(clipboardRef.current.elements, offset);
      updateElements([...renderedElements, ...clones], { forcePersist: true });
      setSelectedIds(clones.map((clone) => clone.id));
    },
    onBringForward: () => adjustZIndex(1),
    onSendBackward: () => adjustZIndex(-1),
    onBringToFront: () => bringToFront(),
    onSendToBack: () => sendToBack(),
    onEnter: () => {
      if (selectedElement && selectedElement.type === 'text' && !editingTextId) {
        startTextEdit(selectedElement.id);
      }
    },
  });

  useEffect(() => {
    isSpacePressedRef.current = isSpacePressed;
  }, [isSpacePressed]);

  const isLoading = brandingContextLoading || loadingProject || workspaceLoading;
  const LayoutComponent = isStandalone ? EditorOnlyLayout : StudioPage;
  const layoutClassName = 'studio-editor-shell';

  if (isLoading) {
    return (
      <LayoutComponent
        className={layoutClassName}
        showTopBarTitle={false}
        topContent={<div className="studio-skeleton-line" />}
        leftContent={<div className="studio-skeleton-block" />}
        rightContent={<div className="studio-skeleton-block" />}
      >
        <div className="studio-editor-loading">
          <div className="studio-skeleton-stage" />
          <div className="studio-skeleton-meta" />
        </div>
      </LayoutComponent>
    );
  }

  if (projectError || !project) {
    return (
      <LayoutComponent className={layoutClassName} showTopBarTitle={false}>
        <div className="studio-editor-state">
          <h3>Design not available</h3>
          <p>{projectError || 'We could not find that design.'}</p>
          <Link className="studio-editor-link" to={studioHomePath}>
            Back to Studio
          </Link>
        </div>
      </LayoutComponent>
    );
  }

  return (
    <LayoutComponent
      className={layoutClassName}
      showTopBarTitle={false}
      topContent={(
        <EditorTopBar
          projectName={project.name || 'Untitled Design'}
          draftName={draftName}
          isEditingName={isEditingName}
          onStartEditingName={handleStartEditingName}
          onNameChange={setDraftName}
          onCommitName={handleCommitName}
          onCancelName={handleCancelName}
          saveStatus={saveStatus}
          zoom={stageControls.zoom}
          onZoomChange={(value) => stageControls.setZoomAtCenter(value / 100)}
          onZoomIn={stageControls.zoomIn}
          onZoomOut={stageControls.zoomOut}
          onFit={stageControls.fitToScreen}
          onResize={openResizeModal}
          showGuides={showGuides}
          onToggleGuides={() => updatePageSettings({ showGuides: !showGuides })}
          onBack={isStandalone ? () => history.push(studioHomePath) : null}
        />
      )}
      leftContent={(
        <LayersPanel
          elements={layeredElements}
          selectedIds={selectedIds}
          onSelect={(id, additive) => {
            if (!additive) {
              setSelectedIds([id]);
              return;
            }
            setSelectedIds((prev) => (
              prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
            ));
          }}
          activeTool={activeTool}
          onToolChange={setActiveTool}
          onAddText={handleAddText}
          onInsertElement={handleInsertElement}
          firebase={firebase}
          franchiseeId={franchiseeId}
          assets={assets}
          assetsLoading={assetsLoading}
          assetsError={assetsError}
          connected={driveConnected}
          needsReconnect={driveNeedsReconnect}
          rootFolderId={studioRootFolderId}
          onRefresh={refreshDriveAssets}
          onConnect={handleConnectDrive}
          onPlaceAsset={handlePlaceAsset}
          onBeginAssetDrag={handleBeginAssetDrag}
          onEndAssetDrag={handleEndAssetDrag}
        />
      )}
      rightContent={(
        <PropertiesPanel
          elements={renderedElements}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          setElementsLocal={setElementsLocal}
          commitElementsToFirestore={commitElementsToFirestore}
          isInteractingRef={isInteractingRef}
          onStartTextEdit={startTextEdit}
          editingTextId={editingTextId}
          artboardWidth={artboardWidth}
          artboardHeight={artboardHeight}
          pageSettings={pageSettings}
          backgroundImageOverride={backgroundImageForRender}
          onUpdatePageSettings={updatePageSettings}
          brandColors={project?.brandColors || []}
          palette={palette}
          photoColors={photoColors}
          onUsePaint={handleUsePaint}
          onRequestEyedropper={startEyedropper}
          imageToolPanel={imageToolPanel}
          onOpenImageTool={handleOpenImageTool}
          onCloseImageTool={handleCloseImageTool}
          onApplyImageCrop={handleApplyImageCrop}
          onCancelImageCrop={handleCancelImageCrop}
          onResetImageCrop={handleResetImageCrop}
          onClearImageEraser={handleClearImageEraser}
          frameCropState={frameCropState}
          onStartFrameCrop={startFrameCrop}
          onCommitFrameCrop={() => stopFrameCrop(true)}
          onCancelFrameCrop={() => stopFrameCrop(false)}
          onResetFrameCrop={resetFrameCrop}
          onFrameContentScaleChange={updateFrameContentScale}
          onDebugFillFrame={handleDebugFillFrame}
          debugMode={debugEnabled}
          assets={assets}
          assetsLoading={assetsLoading}
          onSelectFrameAsset={handleSelectFrameAsset}
          onSaveAsUpload={handleSaveSelectedAsUpload}
          onSelectBackgroundImage={handleSelectBackgroundImage}
          onClearBackgroundImage={handleClearBackgroundImage}
          onToggleBackgroundReposition={handleToggleBackgroundReposition}
          isBackgroundRepositioning={isBackgroundRepositioning}
          onCommitBackgroundReposition={handleCommitBackgroundReposition}
          onCancelBackgroundReposition={handleCancelBackgroundReposition}
          onResetBackgroundReposition={handleResetBackgroundReposition}
          onPreviewPageSettings={updatePageSettingsPreview}
        />
      )}
    >
      <div className="studio-editor-canvas">
        {saveError && (
          <div className="studio-save-banner">
            <div>{saveError}</div>
            {saveDebug && process.env.NODE_ENV !== 'production' && (
              <div className="studio-save-debug">{saveDebug}</div>
            )}
          </div>
        )}
        {(exportStatus || exportError) && (
          <div className="studio-export-banner">
            <div>{exportError || exportStatus}</div>
          </div>
        )}
        <ResizeCanvasModal
          isOpen={resizeModalOpen}
          loading={resizeSaving}
          widthValue={resizeWidth}
          heightValue={resizeHeight}
          unitValue={resizeUnit}
          currentWidth={artboardWidth}
          currentHeight={artboardHeight}
          errors={resizeErrors}
          scaleContent={resizeScaleContent}
          onWidthChange={(value) => handleResizeDimensionChange('width', value)}
          onHeightChange={(value) => handleResizeDimensionChange('height', value)}
          onUnitChange={setResizeUnit}
          onToggleScale={() => setResizeScaleContent((prev) => !prev)}
          onBlurWidth={(value) => setResizeWidth(normalizeDimension(value))}
          onBlurHeight={(value) => setResizeHeight(normalizeDimension(value))}
          onCancel={closeResizeModal}
          onConfirm={applyCanvasResize}
          canSubmit={
            Boolean(resizeWidth)
            && Boolean(resizeHeight)
            && !resizeErrors.width
            && !resizeErrors.height
          }
        />
        <CanvasStage
          viewportRef={viewportRef}
          stageRef={stageRef}
          artboardRef={artboardRef}
          artboardWidth={artboardWidth}
          artboardHeight={artboardHeight}
          pageSettings={pageSettings}
          elements={canvasElements}
          selectedIds={selectedIds}
          editingTextId={editingTextId}
          editingTextValue={editingTextValue}
          onElementPointerDown={handleElementPointerDown}
          onTextStartEdit={startTextEdit}
          onTextChange={handleTextChange}
          onTextCommit={commitTextEdit}
          onTextCancel={cancelTextEdit}
          onWorkspacePointerDown={handleWorkspacePointerDown}
          onWorkspacePointerMove={handleWorkspacePointerMove}
          onWorkspacePointerUp={handleWorkspacePointerUp}
          showGuides={showGuides}
          stageControls={stageControls}
          isSpacePressed={isSpacePressed}
          eyedropperActive={eyedropperActive}
          selectionBounds={transformBounds}
          marquee={marquee}
          resizeHud={resizeHud}
          snapGuides={snapGuides}
          onTransformHandlePointerDown={handleTransformHandlePointerDown}
          onRotatePointerDown={handleRotatePointerDown}
          onAssetDrop={handleAssetDrop}
          onWheelOverride={handleCanvasWheelOverride}
          imageToolPanel={imageToolPanel}
          eraserMode={eraserMode}
          cropTarget={cropTarget}
          cropRect={cropRect}
          onCropPointerDown={handleCropPointerDown}
          frameCropState={frameCropState}
          onFrameCropPointerDown={handleFrameCropPointerDown}
          hoveredFrameId={dragUI.hoveredFrameId}
          debugMode={debugEnabled}
          activeEraserStroke={activeEraserStroke}
          eraserCursor={eraserCursor}
          onImageEraserPointerDown={handleImageEraserPointerDown}
          isBackgroundRepositioning={isBackgroundRepositioning}
        />
        {dragUI.active && dragUI.armed && dragAssetRef.current.payload?.src && (
          <div
            className="studio-drag-ghost"
            style={{
              left: dragUI.clientX + 12,
              top: dragUI.clientY + 12,
              backgroundImage: `url(${dragAssetRef.current.payload.src})`,
            }}
          />
        )}
        {debugEnabled && dragUI.active && (
          <div className="studio-drag-debug">
            <div>drag.active: {String(dragUI.active)}</div>
            <div>drag.armed: {String(dragUI.armed)}</div>
            <div>pointerId: {dragAssetRef.current.pointerId ?? 'n/a'}</div>
            <div>client: {Math.round(dragUI.clientX)},{Math.round(dragUI.clientY)}</div>
            <div>
              rect: {dragDebug.canvasRect
                ? `${Math.round(dragDebug.canvasRect.left)},${Math.round(dragDebug.canvasRect.top)} `
                  + `${Math.round(dragDebug.canvasRect.width)}x${Math.round(dragDebug.canvasRect.height)}`
                : 'n/a'}
            </div>
            <div>zoom: {stageControls.zoom.toFixed(2)}</div>
            <div>pan: {Math.round(stageControls.pan.x)},{Math.round(stageControls.pan.y)}</div>
            <div>world: {Math.round(dragUI.worldX)},{Math.round(dragUI.worldY)}</div>
            <div>hovered: {dragUI.hoveredFrameId || 'none'}</div>
            <div>frames: {renderedElements.filter((el) => el.type === 'frame').length}</div>
            <div>lastDrop: {dragDebug.lastDropAction}</div>
            <div>lastError: {dragDebug.lastError || 'none'}</div>
          </div>
        )}
      </div>
    </LayoutComponent>
  );
};

export default StudioEditorPage;
